import { CONSOLIDATED_BLOG_SLUGS } from "@/lib/redirects";

export type BlogCategory = "winter" | "summer" | "tips" | "guide";

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: BlogCategory;
  emoji: string;
  readTime: string;
  author: string;
  date: string;
  featured?: boolean;
  content: string;
}

export const CATEGORY_LABELS: Record<BlogCategory, string> = {
  winter: "Winter",
  summer: "Summer",
  tips: "Tips & Advice",
  guide: "Resort Guides",
};

export const CATEGORY_THUMB: Record<BlogCategory, string> = {
  winter: "bg-gradient-to-br from-[#2A3F6A] to-[#5A7DAA]",
  summer: "bg-gradient-to-br from-terracotta to-[#E8B87A]",
  tips: "bg-gradient-to-br from-sage to-[#A0B890]",
  guide: "bg-gradient-to-br from-navy to-sage",
};

// Every post ever written. Posts whose topic was consolidated into a main
// guide (see CONSOLIDATION_REDIRECTS in lib/redirects.ts) stay here as source
// material for the guide refreshes, but are not published: their URLs 301.
const ALL_BLOG_POSTS: BlogPost[] = [
  {
    slug: "complete-guide-first-ski-season",
    title: "The Complete Guide to Your First Ski Season",
    excerpt:
      "Everything you need to know before doing your first winter season, from choosing a resort and finding a role to what to pack and how to make the most of your time in the mountains.",
    category: "winter",
    emoji: "🎿",
    readTime: "8 min read",
    author: "Josh, Founder",
    date: "Feb 2026",
    featured: true,
    content: `## Why do a ski season?

There's nothing quite like a winter season. You wake up in the mountains, ski or snowboard on your days off, work alongside people from all over the world, and build friendships that last a lifetime. For many people, a season is the most memorable few months of their life.

But it's also a big decision. You're committing to several months in a new country, often in a physically demanding role, away from home. The more you prepare in advance, the better your season will be.

## Choosing your resort

The first decision is where to go. Popular resorts for first-time seasonaires include:

- **Méribel**: The heart of the Three Valleys. Huge English-speaking community, incredible skiing, lots of independent chalet companies.
- **Val d'Isère**: Legendary resort with a brilliant social scene. Popular with more experienced seasonaires.
- **Morzine**: More affordable than the big-name resorts. Great gateway to the Portes du Soleil.
- **Verbier**: Premium Swiss resort. Higher pay but higher cost of living.

Think about what matters to you: the size of the ski area, the social scene, the type of work available, and your budget.

## Finding a role

Most first-timers go for chalet host or chalet chef roles. These are the backbone of the season. You'll cook, clean, and look after guests in a luxury chalet. It's hard work, but you get accommodation, food, a lift pass, and plenty of ski time.

Other common roles include driver (airport transfers), nanny, bar staff, and hotel receptionist.

The best time to start looking is August–October for a December start. Create your profile on PeakWave, set your availability, and let employers find you. The earlier you start, the more choice you'll have.

## What to pack

Pack less than you think. You'll be living in shared accommodation with limited space. Essentials:

- **Thermals**: You'll wear these every day, on and off the slopes
- **Good base layers**: Merino wool is your friend
- **Waterproof jacket and trousers**: Invest in quality
- **Comfortable work shoes**: You'll be on your feet all day
- **Ski/snowboard gear**: Buy or rent in resort, don't haul it from home
- **Documents**: Passport, visa/work permit, qualifications, police/background check if needed

## Making the most of it

The people who have the best seasons are the ones who say yes to everything. Go to the staff parties, join the ski races, explore the resort on your days off, and be open to new friendships.

It's also important to pace yourself. Seasons are long, and the combination of altitude, physical work, and social life can catch up with you. Look after your body, get enough sleep, and don't burn out in the first month.

Your season will fly by faster than you expect. Enjoy every moment of it.`,
  },
  {
    slug: "ultimate-season-packing-list",
    title: "The Ultimate Season Packing List",
    excerpt:
      "Don't turn up to the Alps without thermals. Here's everything you actually need to pack, and what you can leave behind.",
    category: "tips",
    emoji: "📦",
    readTime: "5 min read",
    author: "Josh, Founder",
    date: "Feb 2026",
    content: `## The golden rule: pack less than you think

You'll be living in shared staff accommodation, often with just a single bed and a small wardrobe. There's no room for three suitcases. Bring what you need and buy anything else in resort. There are shops, and things like toiletries and snacks are easy to find locally.

## Clothing essentials

### For work
- **2–3 smart-casual outfits** if you're front-of-house (chalet host, hotel reception)
- **Chef whites / aprons** if you're a chef (most employers provide these, but check)
- **Comfortable shoes with good grip**: you'll be on your feet 8+ hours a day
- **Waterproof boots** for walking between accommodation and work in the snow

### For skiing / snowboarding
- **Ski jacket and trousers**: waterproof, breathable, warm. This is worth investing in
- **3–4 base layers**: merino wool is the gold standard
- **Thermal leggings**: at least 2 pairs
- **Ski socks**: proper ski socks make a huge difference. Bring 3–4 pairs
- **Helmet**: non-negotiable for safety
- **Goggles**: get ones that fit your helmet
- **Gloves**: waterproof, warm, ideally with a liner
- **Neck warmer / buff**: you'll use this every day
- **Ski/board gear**: most people rent or buy second-hand in resort. Don't haul your setup from home unless it's special to you

### For everyday life
- **Down jacket or puffer**: for evenings and days off
- **Trainers**: for indoors and the gym
- **Going-out clothes**: 2–3 outfits for bars and parties
- **Swimwear**: many resorts have swimming pools and hot tubs

## Documents & admin

- **Passport** (check it's valid for the full season)
- **Work permit / visa** (if applicable; check requirements for your nationality)
- **European Health Insurance Card (EHIC/GHIC)**
- **Travel / ski insurance documents**
- **Driving licence** (essential for driver roles, useful for everyone)
- **Qualification certificates** (food hygiene, BASI, DBS, etc.)
- **Bank card that works abroad**: Monzo, Revolut, or Wise are popular for avoiding foreign transaction fees

## Tech & entertainment

- **Phone + charger**: obvious but essential
- **Portable charger**: cold weather drains batteries fast
- **Headphones**: for downtime and long transfers
- **Laptop or tablet**: for Netflix evenings in the chalet
- **Travel adapter**: check what plug type your destination uses

## What NOT to bring

- **Too many books**: use a Kindle or swap with other seasonaires
- **Full-size toiletries**: buy in resort
- **Expensive jewellery**: shared accommodation + parties = risk
- **Your entire wardrobe**: you'll wear the same 5 things on rotation

## Final tip

Roll your clothes instead of folding them. You'll fit more in and they won't crease. And leave room in your bag for the return journey, because you'll definitely pick up some merch, gear, and souvenirs along the way.`,
  },
  {
    slug: "chalet-chef-vs-chalet-host",
    title: "Chalet Chef vs Chalet Host: Which Role is Right for You?",
    excerpt:
      "Both are essential to a great chalet experience, but the day-to-day is very different. Here's what to expect from each role.",
    category: "winter",
    emoji: "🏔️",
    readTime: "6 min read",
    author: "Josh, Founder",
    date: "Feb 2026",
    content: `## Two roles, one chalet

If you're thinking about doing a ski season, you've probably come across two roles more than any other: chalet chef and chalet host. They're the backbone of the chalet industry, and between them they handle everything from cooking gourmet dinners to making sure guests have the time of their lives.

But the day-to-day reality of each role is very different. Here's what you need to know.

## Chalet Chef

### What you'll do
As a chalet chef, your job is to cook. Breakfast, afternoon tea, and a multi-course dinner, typically 5 or 6 nights a week. The food is a huge part of the guest experience, and many chalets market themselves on the quality of their dining.

You'll plan menus, prep ingredients, cook everything from scratch, and plate up to restaurant standard. Some chalets have a separate kitchen team, but in most small-to-medium chalets, you're the sole chef.

### The schedule
- **Morning**: Breakfast service (usually 7:30–9:30am), then clean up
- **Midday**: Prep for afternoon tea and dinner. This is often your busiest time
- **Afternoon**: Afternoon tea for returning guests (cake, snacks)
- **Evening**: Dinner service, usually 4 courses, served around 7:30–8pm
- **Free time**: Between breakfast cleanup and afternoon prep (roughly 10am–3pm on good days). This is your ski time

### What you'll earn
Chalet chefs typically earn **€1,800–€2,500/month** depending on experience and the chalet company. Accommodation and food are usually included.

### Who it's for
You need genuine cooking ability. This isn't just heating up ready meals. Most employers want at least a Level 2 Food Hygiene certificate, and many prefer candidates with professional kitchen experience or a culinary qualification. If you love cooking and want to combine it with skiing, this is your role.

## Chalet Host

### What you'll do
As a chalet host, you're the face of the chalet. You look after guests from the moment they arrive to the moment they leave. That means everything from serving breakfast and afternoon tea, to cleaning bedrooms, making beds, doing laundry, and ensuring the chalet is spotless for changeover day.

You'll also help with guest requests: recommending restaurants, booking ski lessons, arranging transfers, and generally making sure everyone is having an amazing holiday.

### The schedule
- **Morning**: Serve breakfast, clear up, clean the chalet (bedrooms, bathrooms, communal areas)
- **Midday**: Free time while guests are on the slopes. This is your ski time
- **Afternoon**: Back for afternoon tea service
- **Evening**: Help serve dinner (if the chalet has a chef) or handle evening service

### What you'll earn
Chalet hosts typically earn **€1,200–€1,600/month**. Accommodation, food, and often a lift pass are included.

### Who it's for
You need to be organised, personable, and not afraid of physical work. Cleaning chalets every morning is genuinely hard graft, especially at altitude. But if you're a people person who wants to ski every day and doesn't mind rolling up their sleeves, hosting is an incredible experience.

## So which should you choose?

**Choose chef if:** You love cooking, have kitchen experience, and want higher pay. You're happy working long hours in the kitchen and don't mind less guest interaction.

**Choose host if:** You're a people person, you want more ski time, and you don't mind cleaning. You want a more social role with direct guest contact.

Both roles will give you an unforgettable season. The mountains don't care what your job title is. They're waiting for you either way.`,
  },
  {
    slug: "working-in-europe-after-brexit-visa-guide",
    title: "Working in Europe After Brexit: What Seasonaires Need to Know",
    excerpt:
      "If you're a UK citizen planning a ski season or summer job in Europe, you now need a visa. Here's how the process works for France, Austria, Switzerland and beyond.",
    category: "tips",
    emoji: "🇪🇺",
    readTime: "7 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## The short answer

If you're a UK citizen, you now need a visa or work permit to work in EU countries. The days of freely turning up and getting a job are over. But it's far from impossible, and thousands of British seasonaires still work across the Alps and Mediterranean every year.

## What changed

Before Brexit, UK nationals had the right to live and work anywhere in the EU. Since January 2021, that's no longer the case. You're now treated as a non-EU citizen, which means you need permission to work in each country.

The exact requirements depend on where you're going and what type of work you'll be doing.

## France

France is the most popular destination for British seasonaires, and the process is relatively straightforward. Your employer needs to apply for a work permit (autorisation de travail) on your behalf. Once approved, you apply for a long-stay visa at the French consulate in the UK.

Key points:

- **Your employer handles most of the paperwork**
- **You'll need a confirmed job offer before applying**
- **Processing times vary, so start early** (at least 2-3 months before your start date)
- **The visa is tied to your specific employer and role**

Most established chalet companies and tour operators are experienced with this process and will guide you through it.

## Austria

Austria uses a similar employer-sponsored system. Your employer applies for a work permit, and you then apply for a visa. Austria tends to be slightly more bureaucratic than France, so allow extra time.

## Switzerland

Switzerland was never in the EU, so it had its own bilateral agreement with the UK even before Brexit. Swiss employers can still hire UK nationals, but the quota system and permit process apply. Your employer will handle the work permit application.

Switzerland generally pays higher wages, which partly offsets the higher cost of living. The permit process is well-established and most Swiss employers know the drill.

## Other popular destinations

- **Italy** (Aosta Valley, Dolomites): Employer-sponsored work permit required
- **Spain** (Ibiza, Mallorca for summer): Work visa required, can be more complex
- **Greece** (summer yachting): Depends on vessel flag state and your specific role
- **Croatia, Montenegro**: Growing seasonal markets with varying visa requirements

## What you need to do

1. **Secure a job first.** In almost every case, you need a confirmed job offer before you can apply for a visa. This is why platforms like <a href="/signup?intent=work&amp;next=%2Fjobs&amp;source=blog" style="color:#1C8A99">PeakWave</a> exist: to connect you with employers who will sponsor your permit.

2. **Check your passport.** It needs to be valid for at least 6 months beyond your planned stay and issued within the last 10 years.

3. **Gather your documents.** You'll typically need passport photos, proof of qualifications, a clean criminal record check, and your contract or job offer letter.

4. **Apply early.** Visa processing can take weeks. Don't leave it until the last minute.

5. **Get health insurance.** Your GHIC (Global Health Insurance Card) covers emergency treatment, but most employers and visa applications require additional coverage.

## What about Irish or dual nationals?

If you hold an Irish or other EU passport, none of this applies to you. You can still work freely across the EU. If you have any claim to an EU nationality through parents or grandparents, it's worth investigating before going through the visa route.

## Don't let the paperwork put you off

Yes, it's more admin than it used to be. But the visa process is now routine for most seasonal employers, and they'll support you through it. The biggest mistake is waiting too long to apply. Start looking for roles in the summer, get your job offer locked in, and your paperwork will be sorted well before the season begins.`,
  },
  {
    slug: "best-ski-resorts-first-season",
    title: "Best Ski Resorts to Work a Season in 2026",
    excerpt:
      "The best ski resorts for seasonal work, compared. Honest breakdown of pay, lifestyle, ski area, and social scene across the Alps and North America.",
    category: "guide",
    emoji: "⛷️",
    readTime: "7 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## Choosing where to go

Picking a resort for your first season can feel overwhelming. There are dozens of options across France, Austria, Switzerland, and beyond. The right choice depends on what you want from your season: the size of the ski area, the social scene, the cost of living, and what kind of work is available.

Here's an honest breakdown of the most popular resorts for first-time seasonaires.

## Méribel, France

Méribel is one of the most popular resorts for English-speaking seasonaires, and for good reason. It sits in the centre of the Three Valleys, the largest linked ski area in the world. The international community is huge, so you'll never struggle to make friends.

There's a strong chalet industry here, meaning plenty of chalet host and chef roles. The social scene is brilliant, with iconic bars drawing seasonaires every week.

**Best for:** First-timers who want a big ski area, a ready-made social scene, and plenty of chalet work. <a href="/guides/meribel" style="color:#1C8A99">Read our full Méribel guide.</a>

## Morzine, France

Morzine is more affordable than the big-name resorts and has a relaxed, town-like atmosphere. It's a gateway to the Portes du Soleil, which links across to Switzerland. The terrain suits intermediate skiers well, and the town itself has a good mix of restaurants, bars, and shops.

Accommodation costs are lower here, and there's a growing range of roles available beyond the traditional chalet circuit.

**Best for:** Budget-conscious seasonaires who want a genuine alpine town feel rather than a purpose-built resort. <a href="/guides/morzine" style="color:#1C8A99">Read our full Morzine guide.</a>

## Val d'Isère, France

Val d'Isère has a reputation as one of the best ski resorts in the world, and the skiing lives up to it. The terrain is challenging and varied, with the Espace Killy area connecting to Tignes.

The social scene is legendary. It attracts a slightly more experienced crowd, and the cost of living is higher than Méribel or Morzine. But if skiing quality is your top priority, it's hard to beat.

**Best for:** Confident skiers who want world-class terrain and a lively social scene. <a href="/guides/val-disere" style="color:#1C8A99">Read our full Val d'Isère guide.</a>

## Verbier, Switzerland

Verbier is the premium option. Swiss wages are significantly higher than in France, but so is the cost of everything else. The skiing is exceptional, with steep off-piste runs and stunning views.

The seasonaire community is smaller but tight-knit. Expect a more international crowd. It's worth knowing that Swiss work permits are well-established, and most employers handle the process smoothly.

**Best for:** Those who want higher wages, world-class freeride terrain, and a more intimate season experience. <a href="/guides/verbier" style="color:#1C8A99">Read our full Verbier guide.</a>

## Chamonix, France

Chamonix is a real mountain town, not a purpose-built resort. It sits at the foot of Mont Blanc and attracts serious mountaineers alongside skiers. The vibe is grittier and more independent than the big chalet resorts.

There are fewer traditional chalet host roles here, but plenty of work in bars, restaurants, outdoor shops, and activity companies. If you're drawn to alpinism, ice climbing, or backcountry skiing, Chamonix is the place.

**Best for:** Independent types, experienced skiers, and anyone who wants a mountain town that lives and breathes year-round. <a href="/guides/chamonix" style="color:#1C8A99">Read our full Chamonix guide.</a>

## Whistler, Canada

If you want to go further afield, Whistler is the gold standard for North American seasons. The ski area is enormous, the snow is reliable, and the town has a vibrant scene. You'll need a working holiday visa (IEC) for Canada, which is competitive but achievable for under-35s.

The season runs later than the Alps, often well into April, and the riding is consistently excellent. It's more expensive to get set up, but the experience is unique.

**Best for:** Those looking for something different, with reliable snow and a longer season. <a href="/guides/whistler" style="color:#1C8A99">Read our full Whistler guide.</a>

## How to decide

Ask yourself three questions:

- **What's my budget?** Morzine and smaller Austrian resorts are more affordable. Verbier and Val d'Isère cost more.
- **What kind of skiing do I want?** Beginners and intermediates are well-served almost everywhere. Advanced skiers should look at Val d'Isère, Verbier, or Chamonix.
- **How important is the social scene?** If you want a big English-speaking community and a ready-made social circle, Méribel is hard to beat.

Whatever you choose, you'll have an incredible time. The resort matters less than the attitude you bring. <a href="/browse" style="color:#1C8A99">Browse available roles</a> and start planning your season.`,
  },
  {
    slug: "bar-work-ski-resort-guide",
    title: "What Bar Work in a Ski Resort is Really Like",
    excerpt:
      "Bar work is one of the most social and ski-friendly roles in any resort. Here's what the hours, pay, and lifestyle actually look like.",
    category: "winter",
    emoji: "🍺",
    readTime: "6 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## Not just pulling pints

When people think of ski season jobs, chalet hosting and cooking usually come to mind first. But bar work is one of the most popular and rewarding roles in any ski resort. You'll be at the heart of the social scene, working alongside other seasonaires, and often skiing more than anyone else in the chalet world.

Here's what bar work in a ski resort actually looks like.

## The role

You'll be serving drinks, making cocktails, chatting to guests, and keeping the bar running smoothly. Depending on where you work, you might be in a lively après-ski bar that's packed from 4pm, a late-night club, or a quieter hotel bar.

Most bars want staff who are friendly, quick on their feet, and comfortable working in a loud, busy environment. Previous bar experience helps, but plenty of resorts hire enthusiastic beginners too.

## The schedule

This is where bar work differs most from chalet roles. Your hours tend to be afternoon and evening, which means your mornings are free.

A typical day might look like this:

- **Morning:** Ski or snowboard from first lift until lunchtime
- **Afternoon:** Start your shift around 3-4pm, right as après kicks off
- **Evening:** Work through dinner service and into the night
- **Finish:** Anywhere from 11pm to 2am, depending on the bar

Some bars have split shifts, where you work the après rush, get a break for dinner, then come back for the late crowd. It's not unusual to work six days a week during peak periods.

## The pay

Bar wages in ski resorts are generally lower than chalet roles. Expect around €1,000-€1,400 per month in France, sometimes with accommodation included, sometimes not. Tips can supplement this, especially in busy après bars where rounds are expensive and guests are in holiday mode.

If accommodation isn't included, you'll need to factor in rent for shared housing, which varies hugely by resort. This is worth clarifying before you accept a role.

## The perks

The biggest perk is ski time. Because your shifts are mostly afternoon and evening, you get full mornings on the mountain. Many bar workers ski more than anyone else in the resort.

You'll also be plugged into the social fabric of the resort. Bartenders know everyone: the chalet hosts, the instructors, the guests, the other bar teams. If you're a social person, there's no better role for building a network and having an unforgettable season.

Some bars also offer a lift pass as part of the package, though this is less standard than in chalet roles. Always ask.

## The downsides

Late nights can be tough, especially combined with early morning skiing. The temptation to stay out after your shift and then get up for first lifts is real, and it catches up with most people eventually. Pace yourself.

The work can be physically demanding. You'll be on your feet for long shifts, carrying trays, changing kegs, and cleaning up. If the bar is at altitude, add thin air to the mix.

And if accommodation isn't included, you're spending a significant chunk of your wages on rent. This is the biggest financial downside compared to chalet roles, where room and board are almost always part of the deal.

## How to find bar work

Many bars recruit locally, hiring people who are already in resort. Turning up early in the season and visiting bars in person can be surprisingly effective. But if you want to secure a role in advance, <a href="/browse" style="color:#1C8A99">check the listings on PeakWave</a> or create your profile so employers can find you.

Having bar experience helps, but it's not always essential. What matters more is personality, reliability, and a willingness to work hard.

## Is it right for you?

**Choose bar work if:** You want maximum ski time, love being social, and don't mind late nights. You're OK with potentially lower pay and less certain accommodation.

**Consider other roles if:** You want guaranteed accommodation, prefer a more structured routine, or find late nights draining.

Bar work gives you a season in the thick of the action. If you're the kind of person who thrives on energy and atmosphere, it might be the best job you ever have.`,
  },
  {
    slug: "how-to-save-money-ski-season",
    title: "How to Save Money During Your Ski Season",
    excerpt:
      "A ski season doesn't have to drain your savings. With the right planning and a few smart habits, you can enjoy the mountains without going broke.",
    category: "tips",
    emoji: "💰",
    readTime: "6 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## The reality of season finances

A ski season isn't cheap. Between getting there, buying gear, and the inevitable après sessions, it's easy to burn through your savings faster than expected. But plenty of seasonaires manage to finish the season with money still in the bank, or at least break even. It comes down to planning and a few smart habits.

## Before you go

### Build a buffer

Have at least one month's expenses saved beyond your first pay cheque. Wages often come late in the first month, and you might need to buy gear, pay a deposit on accommodation, or cover transport. Running out of cash in week three is stressful and avoidable.

### Get the right bank account

If you're working in euros, a multi-currency account with Monzo, Revolut, or Wise will save you a fortune on exchange rates and transaction fees. Set this up before you leave. Some employers can pay directly into euro-denominated accounts.

### Buy gear smart

Don't buy everything new. Check second-hand sites, Facebook marketplace groups for your resort, and end-of-season sales. Skis, boots, and boards that are a year or two old perform almost identically to brand-new gear at a fraction of the price.

## During the season

### Take advantage of what's included

If your job comes with accommodation, food, and a lift pass, you're already saving thousands. Make the most of staff meals instead of eating out. A lift pass alone can cost over €1,000 for the season, so a role that includes one is a significant perk.

### Cook at home

Eating out in ski resorts is expensive. A pizza and a beer can easily cost €25-€30. Get into the habit of cooking with your housemates. Bulk cooking pasta, stews, and curries is cheap, social, and much better fuel for skiing than a hangover breakfast.

### Watch the après habit

This is where most season budgets fall apart. Drinks in resort bars are not cheap, and it's easy to spend €50-€80 on a single night out. You don't need to become a hermit, but setting a weekly spending limit for nights out will keep things under control.

Some tips that work:

- **Pre-drinks at home** before going out
- **Stick to beer** over cocktails
- **Alternate** between big nights out and quieter evenings in
- **Take advantage of staff discount** if your bar offers one

### Track your spending

It doesn't need to be complicated. A simple note on your phone where you jot down what you spend each week will make you much more aware of where the money goes. Most people are surprised when they see the numbers.

### Sell your skills

If you have a skill, use it. Plenty of seasonaires pick up extra income through private lessons, personal training, massage, photography, or tutoring. If you're a qualified ski instructor, weekend privates can bring in meaningful cash on top of your main role.

## The big-ticket savings

### Don't buy a season pass if it's included

This sounds obvious, but some people take roles without a pass and then buy one separately. If a pass is important to you, prioritise jobs that include one. <a href="/browse" style="color:#1C8A99">Browse roles on PeakWave</a> and check what's included in the package.

### Share transport costs

Getting to resort can be expensive. Sharing a car or arranging a group transfer splits the cost significantly. Many resort Facebook groups have lift-share threads in the weeks before the season starts.

### Use your time off wisely

On days off, not every activity needs to cost money. Touring, hiking, exploring the village, or just enjoying the views is free. Save the expensive off-piste guides and heliskiing for a special occasion.

## Coming home

Budget for the journey home and have a plan for re-entry. Many seasonaires struggle financially in the weeks after a season because they didn't plan for the gap before their next job starts. Having some savings set aside makes the transition much smoother.

A ski season is one of the best experiences you'll ever have. Being smart with money doesn't mean having less fun: it means the fun lasts longer and you're not stressed about your bank balance while you're living in the mountains.`,
  },
  {
    slug: "winter-vs-summer-season",
    title: "Winter Season vs Summer Season: How to Choose",
    excerpt:
      "Mountains or coastlines? Chalets or yachts? If you're deciding between a winter and summer season, here's an honest comparison of the work, lifestyle, and money.",
    category: "tips",
    emoji: "☀️",
    readTime: "7 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## Two seasons, two completely different lives

The seasonal work world splits broadly into winter and summer. Winter means mountains, snow, chalets, and ski resorts. Summer means coastlines, yachts, watersports, and beach clubs. Both are incredible experiences, but they attract different people and suit different skills.

If you're trying to decide which to do first, or whether to try the other side, here's an honest comparison.

## The work

### Winter

Winter season roles are dominated by the chalet industry. Chalet hosts, chefs, drivers, nannies, and hotel staff make up the bulk of the workforce. Ski and snowboard instructors are another major category, though these require qualifications. The work is physically demanding: cleaning chalets at altitude, cooking multiple courses, and handling changeover days that can run from early morning to late evening.

### Summer

Summer roles are more varied. Yacht crew (deckhands, stewardesses, chefs), watersports instructors (sailing, surfing, diving, paddleboarding), beach bar staff, kids' club leaders, and activity centre coordinators. The work is often outdoors, which is a draw for many people, but heat and long hours on the water bring their own challenges.

## The lifestyle

### Winter

You live in a mountain village, usually in shared staff accommodation. Your days off are spent skiing or snowboarding. The social scene centres around après-ski bars and house parties. The community is tight because everyone lives and works in the same small valley. Seasons typically run December to April.

### Summer

Summer seasons can be based anywhere from the Mediterranean to the Caribbean to Southeast Asia. Yacht crew travel constantly, while land-based roles give you a fixed base in a coastal town. Days off might be spent on the beach, diving, or exploring. The vibe is generally more relaxed than winter, though yacht work can be intense. Seasons vary: May to October in Europe, or year-round in tropical locations.

## The money

Winter chalet roles typically include accommodation, food, and a lift pass on top of a modest wage. You can potentially save money if you're disciplined, because your living costs are largely covered.

Summer varies more. Yacht crew earn well, especially with tips, and your expenses are minimal since you live on board. Land-based summer roles may or may not include accommodation, and if you're paying rent in a Mediterranean resort town, costs can eat into your wages quickly.

## The skills you need

### Winter

Cooking ability matters for chef roles. Cleaning speed and guest-facing skills matter for hosting. Ski instructing requires BASI, CSIA, or equivalent qualifications. Many roles are entry-level and require no prior experience beyond a good attitude and a willingness to work hard.

### Summer

Yachting requires specific certifications: an ENG1 medical, STCW basic safety training, and ideally a Yachtmaster or Day Skipper qualification. Watersports roles need instructor qualifications from bodies like the RYA, PADI, or ISA. Beach and bar roles are more accessible to beginners.

## Which should you do?

**Do a winter season if:**

- You love skiing or snowboarding (or want to learn)
- You want a tight-knit community in one place
- You're drawn to chalet life and mountain living
- You want accommodation and food included as standard

**Do a summer season if:**

- You love the water, warmth, and coastal life
- You want to travel to different places (especially on yachts)
- You have or want to get watersports qualifications
- You're comfortable with more varied accommodation arrangements

## Why not both?

Many experienced seasonaires do back-to-back: a winter in the Alps followed by a summer on the coast or on the water. This "double season" lifestyle is demanding but lets you work year-round doing what you love, building skills and connections across both worlds.

If you're considering your first season of any kind, <a href="/signup?intent=work&amp;next=%2Fjobs&amp;source=blog" style="color:#1C8A99">create your profile on PeakWave</a> and set your availability. Whether it's snow or sun, the best time to start looking is a few months before the season begins.`,
  },
  {
    slug: "how-to-become-ski-instructor",
    title: "How to Become a Ski or Snowboard Instructor",
    excerpt:
      "Getting paid to ski sounds like the dream. Here's what the qualification path actually looks like, what it costs, and how to find your first instructor role.",
    category: "winter",
    emoji: "🎿",
    readTime: "7 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## The dream job?

For many seasonaires, becoming a ski or snowboard instructor is the ultimate goal. You get paid to ski, you work outdoors in the mountains, and your "office" is a chairlift. But getting there takes investment: time, money, and commitment. Here's what the path actually looks like.

## The qualification route

### In the UK (BASI)

The British Association of Snowsport Instructors (BASI) is the standard qualification body for UK-based instructors. The system works in levels:

- **BASI Level 1:** Your entry point. A course that qualifies you to teach beginners in a supervised environment. It's relatively affordable and a good way to test whether instructing is for you.
- **BASI Level 2:** Allows you to teach independently up to intermediate level. This is the minimum most employers want. The course is longer and more demanding, but it opens up real job opportunities.
- **BASI Level 3 and 4:** Advanced qualifications that allow you to teach at higher levels and in more countries. Level 4 (ISTD) is the top qualification and puts you on par with French and Austrian instructors.

### Other systems

If you're not going through BASI, alternatives include:

- **CSIA** (Canadian Ski Instructors' Alliance): Common for those heading to Whistler or other Canadian resorts
- **NZSIA** (New Zealand): Popular for gap year seasons in Queenstown or Wanaka
- **APSI** (Australia): For the Australian ski fields

Each country recognises different qualifications, so check what's accepted where you want to work.

## The cost

This is the part that surprises people. Getting fully qualified is not cheap. Between course fees, accommodation during training, travel, and time off work, the investment to reach Level 2 is significant. Many people fund this by working a chalet season first and using their savings to pay for instructor training.

Some companies offer combined packages: you work for them (as a chalet host or similar) and they subsidise your instructor training alongside the role. These can be a smart way to get qualified without a large upfront cost.

## Where to train

You can take courses in the Alps during winter, at indoor snow centres year-round, or in the southern hemisphere during the northern summer. New Zealand is popular for this, and many people combine training with a short season there, turning their qualification journey into a travel experience.

## Finding work as a newly qualified instructor

With a Level 1, your options are limited. Most ski schools want at least a Level 2 before they'll hire you. Some large tour operators hire Level 1 instructors for children's clubs or beginner groups, but these positions are competitive.

With a Level 2, you can work in many resorts across Europe and North America. The most common employer is a ski school, either the resort's official école du ski or an independent operation. Jobs are seasonal, typically December to April for winter.

When applying, your qualification level matters, but so does your personality. Ski schools want instructors who are patient, fun, and good with people. Guests book private lessons expecting an experience, not just technique. Being able to teach children is especially valued.

## The legal side

In France, teaching skiing is a regulated profession. Only instructors with certain high-level qualifications (or equivalences) can legally teach there. In practice, many BASI-qualified instructors work through approved ski schools under supervised arrangements, especially at Level 2. Your employer will advise on what's permitted.

In Austria and Switzerland, the rules are slightly different and generally more accommodating to foreign-qualified instructors. Canada and New Zealand are open to instructors with local or equivalent qualifications.

## Is it worth it?

The pay for ski instructors varies widely. Private lesson rates can be excellent, especially in premium resorts. Employed instructors at ski schools earn a decent wage, and tips from happy clients can add up. But when you factor in the cost of getting qualified, it can take a season or two to break even.

The real value is in the lifestyle. Instructing is one of the few seasonal roles where skiing is literally your job. If that's what you want from your season, the investment is worth every penny.

<a href="/browse" style="color:#1C8A99">Browse instructor roles on PeakWave</a> and start planning your path to the mountains.`,
  },
  {
    slug: "changeover-day-chalet-host-guide",
    title: "Surviving Changeover Day: A Chalet Host's Guide",
    excerpt:
      "Changeover day is the hardest day of your week as a chalet host. Here's how to get through it efficiently, what to expect, and how to handle the things that go wrong.",
    category: "tips",
    emoji: "🧹",
    readTime: "5 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## The hardest day of your week

If you're a chalet host, changeover day is the day everything happens at once. Guests leave, the chalet gets stripped and deep-cleaned, new guests arrive, and you need everything to be perfect by the time they walk through the door. It's exhausting, relentless, and a rite of passage for every seasonaire.

Here's how to get through it with your sanity intact.

## What actually happens

Changeover day usually falls on a Saturday (sometimes Sunday, depending on the company). The typical timeline looks something like this:

- **7:00-8:30am:** Serve departing guests their final breakfast
- **8:30-9:30am:** Guests check out. Luggage collection, transfers, farewells
- **9:30am-3:00pm:** The big clean. Strip every bed, wash and remake with fresh linen, clean every bathroom, vacuum and mop every floor, restock toiletries, check inventory, prepare welcome trays
- **3:00-4:00pm:** Final check. Everything should look like a show home
- **4:00-6:00pm:** New guests arrive. Welcome drinks, chalet tour, dietary requirements chat
- **Evening:** First dinner service for the new group

That's a long day with no real break.

## Tips from seasoned hosts

### Get your system down

By the third or fourth changeover, you'll have a routine. The trick is working out the most efficient order to tackle rooms. Most experienced hosts recommend starting with stripping all the beds first, getting laundry going immediately (it takes the longest), then working through bathrooms, then bedrooms, then communal areas.

### Prepare the night before

Lay out fresh linen, pre-set welcome trays, check that you have enough toiletries and cleaning supplies. Anything you can do the night before saves you time when it counts.

### Speed over perfection (then go back)

On your first pass, aim for fast and thorough. Get every room to 90% as quickly as possible. Then do a final walk-through at the end to catch anything you missed: a smear on a mirror, a towel folded wrong, a hair in the shower. Fresh eyes spot things you missed in the rush.

### Work with your team

If you share the chalet with a chef or another host, coordinate. Who's doing which rooms? Who handles laundry? Who prepares the welcome food? Good communication makes changeover day bearable. Poor communication makes it chaos.

### Look after yourself

Eat a proper breakfast before the guests leave. You might not eat again until dinner. Keep a water bottle with you. And wear comfortable shoes: you'll cover thousands of steps without ever leaving the building.

## The things that go wrong

No changeover goes perfectly. Here are the classics:

- **Guests who won't leave.** Check-out is usually 9 or 10am, but some guests linger. Be polite but firm. You need the chalet empty to start cleaning.
- **Stains and damage.** Red wine on white linen, broken glasses, mysterious marks on walls. You'll develop an encyclopaedic knowledge of stain removal.
- **Laundry bottlenecks.** If the washing machine breaks or you've got more linen than capacity, everything backs up. Some hosts use the local launderette as a backup.
- **Late arrivals.** New guests sometimes arrive hours before check-in. Have a plan: a recommended café, a local walk, somewhere they can wait while you finish.

## Why it matters

Changeover day is exhausting, but it's also the most important day of the guest experience. First impressions are everything. When guests walk into a spotless chalet with fresh flowers, a welcome drink, and a warm greeting, they're immediately relaxed. That sets the tone for the whole week.

If you can master changeover day, you can handle anything the season throws at you. And on Saturday evening, when the new guests are settled and you finally sit down, the sense of achievement is real.

The rest of the week, by comparison, feels easy.`,
  },
  {
    slug: "getting-into-yachting-no-experience",
    title: "Getting Into Yachting with No Experience",
    excerpt:
      "Yachting is one of the most exciting seasonal careers you can get into, but you can't just show up. Here's what you need, where to start, and what your first season will look like.",
    category: "summer",
    emoji: "⛵",
    readTime: "7 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## A different kind of season

Yachting is one of the most exciting seasonal careers you can get into, but it's also one of the most misunderstood. People imagine the glamour: superyachts, exotic ports, wealthy guests. The reality involves long hours, hard physical work, and living in a tiny cabin with crew you've just met. But it can also be genuinely life-changing, with the chance to travel the world, earn well, and build a career that lasts far beyond a single season.

Here's how to get started with no experience.

## The essential certifications

Unlike chalet work, you can't walk into yachting without qualifications. There are a few non-negotiable certificates you need before any reputable yacht will hire you:

- **STCW (Standards of Training, Certification and Watchkeeping):** The basic safety training required by maritime law. Covers fire safety, sea survival, first aid, and personal safety. Courses typically run for about 5 days.
- **ENG1 Medical Certificate:** A medical fitness certificate issued by an MCA-approved doctor. Lasts for 2 years.
- **Food Hygiene Certificate (Level 2):** Essential if you're going for a stewardess or chef role. Quick, affordable, and available online.

These three will get you to the starting line. Many training centres offer combined packages that cover everything in 1-2 weeks.

## Optional but valuable

- **Powerboat Level 2 (RYA):** Shows you can handle tenders and small boats. Many captains prefer crew with this.
- **Day Skipper or Yachtmaster:** Not required for entry-level roles, but signals serious commitment and opens doors faster.
- **Wine and cocktail courses:** Useful for stewardess roles on high-end yachts where service standards are demanding.

## The main entry-level roles

### Deckhand

Responsible for the exterior of the yacht: cleaning, maintenance, anchoring, handling tenders, and water toys. This is physical, outdoor work. You'll be scrubbing teak, polishing stainless steel, and hauling anchor chain. It helps to be fit, practical, and comfortable around water.

### Stewardess

Responsible for the interior: housekeeping, table service, laundry, and guest care. Think of it as chalet hosting on water, with higher service standards and tighter spaces. Attention to detail, presentation skills, and a calm temperament matter.

### Junior chef

Smaller yachts sometimes take on junior chefs or cook/stews (a combined role). You'll need genuine cooking ability and usually a food hygiene certificate at minimum.

## Where to look for your first job

The two main yachting hubs are:

- **Antibes, France:** The epicentre of the Mediterranean yachting world. Walk the docks in spring (March to May) and you'll find yachts crewing up for the summer season.
- **Fort Lauderdale, USA:** The hub for Caribbean and transatlantic yachts. Peak hiring is October to December.

"Dockwalking," which means physically going from yacht to yacht with your CV, is still one of the most effective ways to get hired. Dress smartly, bring printed CVs in waterproof folders, and be ready to start immediately. Captains often hire on the spot for last-minute crew needs.

Online, there are several crew agencies and job boards specific to yachting. Having your profile on <a href="/signup?intent=work&amp;next=%2Fjobs&amp;source=blog" style="color:#1C8A99">PeakWave</a> with your certifications listed makes you visible to yacht operators looking for crew.

## What to expect in your first season

The hours are long, especially during charters (when guests are on board). Days of 14-16 hours are normal during busy periods. Between charters, the pace is more relaxed, with time for maintenance and exploring ports.

Since you live on board and food is provided, your expenses are minimal. Many crew save the majority of their wages. Tips during charters can be substantial, sometimes significantly increasing your monthly income.

## The reality check

Yachting isn't for everyone. You'll share a tiny cabin, have limited privacy, and be away from home for months. The hierarchy on board is strict, and you're expected to be professional at all times. But if you're adaptable, hardworking, and excited by the idea of waking up in a different port every week, it's one of the best career paths the seasonal world has to offer.`,
  },
  {
    slug: "how-to-become-a-seasonaire",
    title: "How to Become a Seasonaire: The Complete Guide",
    excerpt:
      "Everything you need to know about becoming a seasonaire. Types of seasonal work, how to find jobs, what it costs, visas, and tips from people who've done it.",
    category: "guide",
    emoji: "🌍",
    readTime: "10 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## What is a seasonaire?

A seasonaire is someone who works a season abroad. That might mean spending a winter in the French Alps working as a chalet host, a summer crewing yachts in the Mediterranean, or a few weeks working backstage at a music festival. The word comes from the French "saisonnier" (seasonal worker), and it has become the catch-all term for anyone living and working somewhere temporarily, usually in a beautiful location, usually having the time of their life.

Seasonaires aren't just gap year students. You'll find people in their twenties, thirties, and beyond. Career changers, couples, solo travellers. The common thread is simple: the desire to combine work with adventure.

## Types of seasonal work

### Winter (ski season)
The classic. Most winter seasonaires head to the Alps (France, Austria, Switzerland) or North America (Whistler, Colorado, Lake Tahoe). Common roles include chalet host, chalet chef, ski/snowboard instructor, bar staff, hotel receptionist, nanny, driver, and resort rep.

Winter seasons typically run from December to April, though some resorts open as early as November and close as late as May.

### Summer (yachting, watersports, hospitality)
Summer seasons centre around the Mediterranean, with yacht crew, watersports instructors, hotel and villa staff, and beach club workers in high demand. Greece, Croatia, Spain, the South of France, and Turkey are popular destinations.

Summer seasons usually run from April or May through to September or October.

### Festivals
A growing part of the seasonal world. Festival work covers everything from bar staff and security to stage management and catering. The UK festival circuit (Glastonbury, Reading, Bestival, Boomtown) runs from May to September, with similar circuits across Europe.

Festival roles tend to be shorter contracts: a few days to a few weeks at a time.

### Year-round and niche seasons
Some seasonaires work back-to-back: a winter in the Alps followed by a summer in the Med. Others find year-round work in destinations like Chamonix (which has both a winter and summer season) or move between hemispheres, chasing snow from the Alps to New Zealand or Japan.

There's also farm work (fruit picking, harvest seasons), camp counselling (US summer camps), and adventure tourism (safari lodges, trekking guides).

## How to find seasonal work

### The old way
Traditionally, finding a season meant scrolling through resort-specific Facebook groups, emailing chalet companies one by one, and hoping someone replied. It worked, but it was slow and hit-or-miss.

### The PeakWave way
PeakWave lets you create one profile with your experience, qualifications, languages, and availability. You set which seasons and regions you're interested in, and employers come to you. No applications, no cover letters, no chasing. It's free for both sides.

You can [create your profile here](/signup) in under two minutes.

### Other channels
Recruitment agencies (Natives, Season Workers, Jobs in the Alps) are still active but typically charge employers, and the process can be slower. Direct approaches to companies via their websites or Instagram can work too, especially for smaller independent chalets.

## What it costs to get started

The good news is that getting started as a seasonaire is relatively cheap compared to other travel experiences. Most employers provide accommodation and often food as well.

Your main upfront costs will be:
- **Travel to resort**: flights or driving, typically £50 to £200
- **Insurance**: travel and ski/activity insurance, around £100 to £200 for a season
- **Visa fees**: varies by country, £50 to £300 if required
- **Gear**: ski/snowboard equipment if you don't already own it (many people rent or buy second-hand in resort)
- **Spending money**: for the first few weeks before your first pay cheque arrives. Budget around £300 to £500

Many seasonaires arrive with less than £1,000 saved and manage fine, because accommodation and food are covered.

## Visas and right to work

### EU/EEA nationals
If you hold an EU or EEA passport, you can work freely in any EU country. No visa needed. Switzerland has bilateral agreements that also allow EU nationals to work there with minimal paperwork.

### UK nationals (post-Brexit)
Since Brexit, UK citizens need a visa or work permit to work in most EU countries. France offers a "working holiday" style visa for under-35s, and many chalet companies handle the paperwork. Austria and Switzerland have their own permit systems.

Check the specific requirements for your destination well in advance. PeakWave has a [seasonal work visa guide](/guides/seasonal-work-visa-guide).

### Other nationalities
Visa requirements vary widely. Australia and New Zealand nationals often have working holiday agreements with various countries. For everyone else, check the immigration rules of your destination country.

## What to expect from your first season

### The highs
The lifestyle is genuinely special. You'll ski or surf on your days off, make friends from all over the world, and experience a place deeply rather than as a tourist. Many people describe their first season as one of the best periods of their life.

### The realities
It's also hard work. Early mornings, long days, and physically demanding roles are common. You'll be living in shared accommodation with limited personal space. The pay is modest (typically €1,000 to €2,500/month depending on role, location, and experience). And you'll be far from home.

### The social side
The seasonaire community is one of the best parts. You'll be surrounded by people who made the same choice to prioritise experience over routine. Staff nights out, après-ski, beach days, mountain hikes: the social calendar fills itself.

## Tips from experienced seasonaires

1. **Start early.** The best roles fill up months in advance. For a winter season starting in December, start looking in August or September. [Create your PeakWave profile](/signup) as early as possible.

2. **Be flexible on location.** If you're set on one specific resort, you're limiting your options. Consider a few different places and let the right opportunity come to you.

3. **Get your documents sorted.** Passport, visa, insurance, qualifications (food hygiene, first aid, BASI, etc.). Having everything ready makes you a more attractive candidate.

4. **Pack light.** You'll be living in a small room. Bring essentials and buy the rest in resort. Check out our [packing list guide](/blog/ultimate-season-packing-list).

5. **Save a buffer.** Have enough money to cover your first few weeks before you get paid.

6. **Say yes to everything.** The people who have the best seasons are the ones who are open to new experiences, new friendships, and new challenges.

## Ready to get started?

The seasonal world is open to anyone willing to work hard and embrace the adventure. Whether you're straight out of university, taking a career break, or just looking for something different, there's a season out there with your name on it.

[Create your free profile on PeakWave](/signup) and start getting discovered by employers today.`,
  },
  {
    slug: "how-to-become-a-chalet-host",
    title: "How to Become a Chalet Host: Pay, Skills & Tips",
    excerpt:
      "Everything you need to know about chalet host jobs. What the role involves, skills you need, typical pay, best resorts, and how to get hired for your first season.",
    category: "winter",
    emoji: "🏠",
    readTime: "9 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## What does a chalet host actually do?

A chalet host (sometimes called a chalet girl or chalet boy, though most companies now use the gender-neutral term) is the person who runs the front of house in a ski chalet. You're responsible for making sure guests have an amazing holiday. That means cooking breakfast, baking afternoon tea, serving dinner, cleaning the chalet, making beds, doing laundry, and generally being the friendly, organised person who keeps everything running smoothly.

It's one of the most popular seasonal roles for first-timers, and for good reason: you get accommodation, food, a lift pass, and plenty of time to ski.

## A typical day as a chalet host

### Morning (6:30am to 10:30am)
You'll be up early to prepare and serve breakfast. In most chalets, this means a cooked breakfast (eggs, bacon, pastries, fresh fruit, coffee) laid out for guests by 8am. Once guests head out to the slopes, you clean up the kitchen, then start on the chalet: making beds, cleaning bathrooms, vacuuming, mopping, and restocking supplies.

Changeover days (usually Saturday) are the hardest. You'll deep clean the entire chalet between one set of guests leaving and the next arriving. This is proper physical work.

### Midday (10:30am to 3:30pm)
This is your time. Most hosts ski every day during this window. The lifts are open, the guests are out, and the chalet is clean. This is what makes the job worth it.

### Afternoon (3:30pm to 5:00pm)
Afternoon tea. You'll bake a cake or prepare snacks for guests returning from the slopes. This is your chance to chat with guests, recommend restaurants, help with ski lesson bookings, or arrange transfers.

### Evening (6:00pm to 9:30pm)
Dinner service. In chalets with a separate chef, you'll set the table, serve courses, pour wine, and clear up. In smaller chalets, you might cook a simple supper yourself. After dinner, clean the kitchen and you're done for the night.

### Day off
Most hosts get one or two full days off per week. Use them wisely: ski a different resort, explore the town, rest, or join the inevitable staff night out.

## What skills do you need?

You don't need formal qualifications to become a chalet host. What you do need is:

- **Organisation**: you'll be managing breakfast, cleaning, baking, and dinner in a single day
- **People skills**: you're the face of the chalet. Guests expect warmth, helpfulness, and a smile
- **Cooking ability**: you don't need to be a chef, but you need to produce a solid breakfast and a decent afternoon tea. Many companies provide recipes and training
- **Physical stamina**: cleaning chalets at altitude is hard work, especially on changeover day
- **Reliability**: guests are paying a lot for their holiday. Showing up on time and doing the job well matters

### Helpful extras
- **Food Hygiene Level 2 certificate**: many employers require this, and you can do it online in a few hours for around £20
- **First aid certificate**: not usually required but impressive
- **Driving licence**: useful if the company needs drivers for airport transfers
- **Language skills**: French is helpful in France, but not essential. Most chalet companies operate in English

## How much do chalet hosts earn?

Pay varies by company, resort, and experience level. As a rough guide:

- **France**: €1,200 to €1,600/month
- **Austria**: €1,400 to €1,800/month (Austrian employment law tends to push wages higher)
- **Switzerland**: CHF 2,000 to CHF 2,800/month (higher, but cost of living is too)

On top of your salary, most companies provide:
- Accommodation (shared staff housing)
- Food (meals in the chalet or a food allowance)
- Lift pass (season pass for the resort)
- Travel insurance (some companies, not all)

Tips from guests are not guaranteed but can add up over the season.

### Can you save money?

Yes. Your biggest expenses (rent, food, ski pass) are covered. If you're sensible with your spending, it's realistic to save £1,000 to £3,000 over a season. The main drain is social: bars, restaurants, and nights out can add up quickly.

## Best resorts for chalet host jobs

### Méribel, France
The heart of the Three Valleys and home to a huge number of independent chalet companies. More chalet host jobs here than almost anywhere else. Big English-speaking community. [Read our Méribel guide](/ski/meribel).

### Val d'Isère, France
Legendary resort with a brilliant social scene. Slightly more competitive for jobs, but the experience is worth it. [Read our Val d'Isère guide](/ski/val-disere).

### Morzine, France
More affordable than the bigger resorts and a great entry point for first-timers. Part of the huge Portes du Soleil ski area. [Read our Morzine guide](/ski/morzine).

### Verbier, Switzerland
Premium Swiss resort. Higher wages but a higher cost of living. Beautiful mountain setting and a lively social scene. [Read our Verbier guide](/ski/verbier).

### Chamonix, France
Serious skiing with a year-round mountain town feel. Fewer traditional chalet operations, but plenty of boutique and luxury chalets. [Read our Chamonix guide](/ski/chamonix).

## How to get hired

### Timing
Start looking in August or September for a December start. The bigger companies recruit early (some open applications in June), while smaller independent chalets tend to hire later (September to November). Last-minute roles come up even in December, but you'll have more choice if you start early.

### Where to look
The fastest way is to [create a profile on PeakWave](/signup). Set your availability for winter season, add "chalet host" to your roles, and let employers find you. It's free and takes less than two minutes.

You can also [browse current employers](/browse?role=chalet-host) looking for chalet hosts right now.

### What employers look for
- A positive, can-do attitude
- Relevant experience (hospitality, customer service, cooking)
- Food Hygiene certificate (Level 2)
- Flexibility and willingness to muck in
- A genuine enthusiasm for the mountains and the season lifestyle

### The interview
Most interviews happen over video call. Expect questions about your cooking experience, how you handle difficult guests, and why you want to do a season. Be yourself, show enthusiasm, and come prepared with questions about the chalet, the team, and the role.

## Common mistakes to avoid

1. **Only applying to one resort.** Cast a wider net and you'll get hired faster.
2. **Underestimating the work.** Chalet hosting is physically demanding. Go in with realistic expectations.
3. **Spending everything you earn.** Budget for social spending and try to save something.
4. **Not getting Food Hygiene certified.** It's cheap, quick, and many companies require it. Just do it before you apply.
5. **Leaving it too late.** The best jobs go to candidates who apply early with complete profiles.

## Is chalet hosting right for you?

If you're a people person who doesn't mind early mornings, hard physical work, and sharing living space with colleagues, chalet hosting could be the perfect way to spend a winter. You'll ski nearly every day, make lifelong friends, and have an experience that a regular job simply can't offer.

Ready? [Create your free profile on PeakWave](/signup) and start your chalet host journey.`,
  },
  {
    slug: "do-you-get-paid-ski-season",
    title: "Do You Get Paid to Do a Ski Season?",
    excerpt:
      "Honest breakdown of ski season pay, accommodation, perks, and costs. Find out how much you can earn and whether you'll actually save money.",
    category: "tips",
    emoji: "💰",
    readTime: "7 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## The short answer

Yes, you get paid. A ski season is a real job with a real salary. But the financial picture is more nuanced than just the number on your payslip. The reason people do seasons isn't the money; it's the lifestyle. That said, most seasonaires cover their costs, enjoy an incredible experience, and some even come home with savings.

Let's break it all down.

## How much do ski season jobs pay?

Pay varies significantly depending on your role, the resort, the country, and the company. Here are realistic monthly ranges:

### France (Alps)
- **Chalet host**: €1,200 to €1,600/month
- **Chalet chef**: €1,800 to €2,500/month
- **Ski/snowboard instructor**: €1,400 to €2,200/month (qualified BASI Level 2+)
- **Bar/restaurant staff**: €1,200 to €1,500/month
- **Hotel receptionist**: €1,300 to €1,600/month
- **Nanny**: €1,200 to €1,800/month
- **Driver**: €1,200 to €1,500/month

### Austria
Wages are generally slightly higher than France due to Austrian employment law. Expect 10-20% more across most roles.

### Switzerland
Significantly higher wages (CHF 2,000 to CHF 3,500+/month depending on role), but the cost of living is also much higher. You'll earn more but spend more.

### North America (Whistler, Colorado, etc.)
- Pay is typically CAD/USD $15 to $25/hour for hospitality roles
- Ski instructors can earn more, especially with tips
- Housing is often subsidised but rarely free

## What's included beyond your salary?

This is where ski season jobs get interesting. Most European chalet and hotel employers include some or all of the following:

### Accommodation
Usually provided free or heavily subsidised. You'll share a room (or apartment) with other staff. It's not luxury, but it's warm and close to the slopes. In the Alps, this alone saves you €500 to €1,000/month compared to renting privately.

### Food
Many chalet companies feed you: staff meals at the chalet or a food allowance. Hotel staff often eat at the hotel. This saves another €200 to €400/month.

### Lift pass
Most employers provide a season lift pass, worth anywhere from €500 to €1,200+ for the season depending on the resort. In the Three Valleys, a season pass costs over €1,000 at retail.

### Insurance
Some companies include travel and ski insurance. Others don't, so check before you accept a role. Budget around €100 to €200 if you need to buy your own.

## What will you actually spend?

Even with accommodation and food covered, you'll have expenses:

- **Social spending**: This is the biggest one. Après-ski drinks, nights out, restaurant meals. Budget €200 to €600/month depending on your lifestyle
- **Phone and internet**: €10 to €30/month
- **Transport**: minimal if you live close to work, more if you drive
- **Gear**: rental or purchase of ski/snowboard equipment if you don't own any. Budget €100 to €300 for the season (second-hand is your friend)
- **Clothing and miscellaneous**: €50 to €100/month

### The reality
A social seasonaire in a popular resort might spend €400 to €600/month beyond their covered costs. A more frugal one might spend €150 to €250.

## Can you actually save money?

Yes, but it depends on your role, your spending habits, and your priorities.

**Realistic savings scenarios:**

**Chalet host in Méribel, moderate social life:**
- Salary: €1,400/month
- Accommodation, food, lift pass: included
- Monthly spending: €400
- Monthly savings: €1,000
- Season savings (4 months): €4,000

**Chalet chef in Val d'Isère, active social life:**
- Salary: €2,200/month
- Accommodation, food, lift pass: included
- Monthly spending: €600
- Monthly savings: €1,600
- Season savings (4 months): €6,400

**Bar worker in Morzine, big social life:**
- Salary: €1,300/month
- Accommodation: included (food: not always)
- Monthly spending: €700
- Monthly savings: €600
- Season savings (4 months): €2,400

**Ski instructor in Verbier (qualified):**
- Salary: CHF 2,800/month
- Accommodation: subsidised (you pay CHF 400)
- Monthly spending: CHF 800
- Monthly savings: CHF 1,600
- Season savings (5 months): CHF 8,000

These are realistic mid-range estimates. Some people save more, some save less. The point is that ski season jobs are real jobs that pay real money, and with your biggest expenses covered, saving is very achievable if you want to.

## How does this compare to a normal job?

If you're earning €1,400/month as a chalet host, that might sound low compared to a UK salary. But consider what's included. No rent (saving you £600 to £1,000/month in a UK city), no food costs (saving £200 to £400/month), a free lift pass (worth €1,000+), and you're living in the Alps skiing every day.

The effective value of the package is much higher than the headline salary suggests.

## Tips and bonuses

Tips from guests are not guaranteed but can add up, especially for chalet hosts and chefs. In premium chalets, end-of-week tips of €50 to €200 per guest group are common. Over a full season, that might add €500 to €2,000 to your earnings.

Ski instructors working privately or for high-end schools can earn significantly more in tips, particularly from private lessons.

## The bottom line

You won't get rich doing a ski season. But you'll earn enough to live well, ski every day, and come home with money in your pocket if you're sensible. Most seasonaires describe the financial side as "breaking even with an incredible experience included" at worst, and "saving a few thousand while having the best winter of your life" at best.

The real value of a season isn't measured in euros. It's the friends, the skiing, the mountain sunrises, and the stories you'll tell for years.

Ready to find a paid ski season role? [Browse current opportunities on PeakWave](/browse) or [create your free profile](/signup) to let employers find you.`,
  },
  {
    slug: "how-long-is-a-ski-season",
    title: "How Long Is a Ski Season Job?",
    excerpt:
      "Ski season length by resort and region. From December to April in the Alps, to year-round work in places like Chamonix. Full breakdown inside.",
    category: "guide",
    emoji: "📅",
    readTime: "6 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## The quick answer

A typical ski season lasts 4 to 5 months. In the European Alps, that usually means December to April. In North America, November to April or May. But the exact length depends on where you go, what role you're doing, and whether the snow cooperates.

Let's break it down by region.

## European Alps

### France
Most French resorts open in early to mid December and close in mid to late April. The core season for chalet companies and hospitality staff is roughly 18 to 20 weeks.

- **Typical contract**: Early December to mid/late April
- **Peak period**: February (school holidays across Europe)
- **Early/late options**: Some high-altitude resorts like Val d'Isère and Tignes open in late November. Val Thorens sometimes runs until early May.

Popular French resorts: [Méribel](/ski/meribel), [Val d'Isère](/ski/val-disere), [Morzine](/ski/morzine), [Chamonix](/ski/chamonix).

### Austria
Austrian seasons are similar to France, typically December to April. Austrian resorts tend to be more reliable for late-season snow thanks to higher altitudes and better snowmaking.

- **Typical contract**: December to April (18 to 20 weeks)
- **Notable**: Austrian employment law is more structured, with minimum wage requirements and formal contracts. This generally means slightly better pay and conditions than France.

### Switzerland
Swiss seasons typically run December to April, though some high-altitude resorts extend longer.

- **Typical contract**: December to April
- **Notable**: [Verbier](/ski/verbier) usually closes mid to late April. Zermatt has glacier skiing that runs almost year-round.

## North America

### Whistler, Canada
Whistler has one of the longest seasons in North America, typically running from late November through to late May, and sometimes into June on the upper mountain.

- **Typical contract**: November to April/May (5 to 6 months)
- **Notable**: longer season means more earnings potential. [Read our Whistler guide](/ski/whistler).

### Colorado (Vail, Breckenridge, Aspen)
Most Colorado resorts open mid to late November and close in mid April. Arapahoe Basin sometimes stays open until June.

- **Typical contract**: November to April (5 months)

### Other US resorts
Jackson Hole, Park City, Mammoth, and Lake Tahoe all have seasons of roughly 5 months (November/December to April/May).

## Japan
Japanese ski seasons are shorter but intense, with some of the best powder snow in the world.

- **Typical season**: December to March (3 to 4 months)
- **Top resorts**: Niseko, Hakuba, Furano
- **Notable**: very different culture, limited English-speaking roles, but an incredible experience

## Southern hemisphere

### New Zealand
- **Season**: June to October (4 to 5 months)
- **Top resorts**: Queenstown (Remarkables, Coronet Peak), Wanaka (Cardrona, Treble Cone)
- **Notable**: Great option for doing a "double winter" (European winter followed by NZ winter)

### Australia
- **Season**: June to September (3 to 4 months, shorter than NZ)
- **Top resorts**: Thredbo, Perisher, Falls Creek

## What about summer seasons?

Many seasonaires do back-to-back seasons. A winter in the Alps followed by a summer in the Mediterranean is a classic combination. Summer seasons typically run:

- **Mediterranean yacht/watersports**: April to October (6 months)
- **European hotel/hospitality**: May to September (4 to 5 months)
- **UK festivals**: May to September (intermittent contracts, 1 to 4 weeks each)

## Year-round seasonal work

Some destinations offer both winter and summer work, meaning you can stay in one place all year:

- **Chamonix**: busy ski resort in winter, adventure sports hub in summer. [Read our guide](/ski/chamonix).
- **Zermatt**: glacier skiing in summer, full resort operation in winter.
- **Queenstown, NZ**: skiing in winter, adventure tourism in summer.

## How contract lengths work

Most seasonal contracts are fixed-term. You'll agree on start and end dates before you arrive. Some things to know:

- **Early termination**: if you leave before your contract ends, you may forfeit your return travel or end-of-season bonus.
- **Extensions**: if the season runs long (good snow years), some companies offer extensions. These are usually optional.
- **Changeover periods**: some companies have a mid-season break or staff changeover. Check whether your contract covers the full season or just half.

## When to start looking

As a general rule, start your job search 3 to 6 months before the season starts:

- **For a December start**: start looking in July to September
- **For a November start (North America)**: start looking in June to August
- **For summer season**: start looking in January to March

The earlier you start, the more choice you'll have. [Create your free PeakWave profile](/signup) now and set your availability for the season you want. Employers will find you when they're hiring.

You can also [browse current listings](/browse) to see what's available.`,
  },
  {
    slug: "festival-jobs-uk",
    title: "Festival Jobs UK: How to Work Music Festivals",
    excerpt:
      "How to get festival jobs in the UK. Types of roles, pay, what to expect, and the best festivals to work at. Your guide to the UK festival circuit.",
    category: "summer",
    emoji: "🎪",
    readTime: "8 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## Why work festivals?

Working a music festival is one of the best ways to experience live music, meet incredible people, and earn money at the same time. You get behind-the-scenes access, free entry (saving you hundreds on tickets), and the buzz of being part of something huge.

The UK has one of the richest festival scenes in the world. From the legendary Glastonbury to boutique events like Wilderness and End of the Road, there are hundreds of festivals each summer looking for staff.

## Types of festival jobs

### Bar staff
The most common festival role. You'll serve drinks from temporary bars across the festival site. It's fast-paced, sociable, and usually pays well (tips on top of hourly rates are common at some festivals). Long shifts during peak times, but the atmosphere is electric.

**Pay**: £10 to £14/hour plus tips
**Experience needed**: some bar experience helps but isn't always essential. Speed and a good attitude matter more.

### Catering and food stalls
Working in festival food stalls, backstage catering, or crew kitchens. Roles range from cooking to serving to prep work. Hours are long during service but you often get downtime between meals.

**Pay**: £10 to £15/hour, sometimes with meals included
**Experience needed**: food hygiene certificate is often required. Kitchen experience is a plus.

### Security and stewarding
SIA-licensed security roles or volunteer steward positions. Security staff manage entrances, monitor crowd safety, and handle incidents. Stewards guide people, check tickets, and help with general festival operations.

**Pay**: Security: £12 to £18/hour (SIA licence required). Stewards: often unpaid but get free entry and camping.
**Experience needed**: SIA licence for security roles. Stewarding often requires no experience.

### Stage and production crew
Building stages, rigging lights, managing sound, running backstage operations. These are more specialised roles but incredibly rewarding if you're into the technical side of live events.

**Pay**: £12 to £25/hour depending on skill level
**Experience needed**: varies. Entry-level roles (stage hand, cable runner) need minimal experience. Technical roles (sound engineer, lighting designer) need qualifications.

### Litter picking and site crew
Not glamorous, but essential. Site crew handle everything from setting up before the festival to cleaning up after. Litter picking during and after the event is always in demand.

**Pay**: £10 to £12/hour, or sometimes volunteer (free ticket + camping)
**Experience needed**: none. Just a willingness to get stuck in.

### Campsite management
Managing camping areas, checking passes, helping festival-goers find their pitch, and dealing with any campsite issues. Usually involves early mornings and late nights.

**Pay**: £10 to £14/hour
**Experience needed**: customer service experience helpful.

### Merchandise and retail
Selling band merch, festival merchandise, or working at pop-up shops on site. Less physically demanding than bar or catering work but still busy during peak times.

**Pay**: £10 to £12/hour plus sometimes commission
**Experience needed**: retail experience is a bonus.

### Childcare and family areas
Larger festivals with family camping areas need childcare workers, entertainers, and activity leaders. If you have experience working with children, this can be a great fit.

**Pay**: £10 to £15/hour
**Experience needed**: DBS check usually required. Childcare qualifications are a strong advantage.

## How much can you earn?

Festival work is typically short-term (3 to 7 days per event), so it's not a full-time income. But the money adds up, especially if you work multiple festivals across the summer.

**Example scenario:**
- 5 festivals across the summer
- Average 4 working days per festival
- Average £12/hour, 10 hours/day
- Total: 5 x 4 x 10 x £12 = £2,400

Some experienced festival workers who do the full circuit (May to September) earn £3,000 to £5,000 over the summer, plus free entry to all the events they work.

## Best UK festivals to work at

### Glastonbury
The big one. Working Glastonbury is a rite of passage for festival lovers. Huge demand for staff across all departments. Competition for roles is high, so apply early.

**When**: Late June (5 days)
**Size**: 200,000+ attendees

### Reading and Leeds
Twin festivals running the same weekend in August. Massive lineups, big production, lots of bar and catering roles.

**When**: Late August bank holiday weekend
**Size**: 90,000+ each site

### Boomtown Fair
Independent festival near Winchester with an incredible immersive theme. Known for great working conditions and a loyal returning crew.

**When**: August
**Size**: 70,000+

### Download
Rock and metal festival at Donington Park. If you're into heavier music, this is the one. Consistent demand for bar and security staff.

**When**: June
**Size**: 80,000+

### Wilderness
Boutique festival in Oxfordshire combining music, food, wellness, and arts. Smaller and more upmarket than the big festivals. Good for food and hospitality roles.

**When**: August
**Size**: 30,000+

### Green Man
Beautiful independent festival in the Brecon Beacons, Wales. Music, literature, science, and nature. Smaller scale with a strong volunteer programme.

**When**: August
**Size**: 25,000+

### Latitude
Arts and music festival in Suffolk. Family-friendly with a diverse programme. Good variety of roles available.

**When**: July
**Size**: 40,000+

## How to find festival work

### Direct applications
Many festivals recruit through their own websites. Check the "Work With Us" or "Volunteering" sections from February onwards.

### Festival staffing agencies
Companies like Festaff, Hotbox Events, Bar Hire, and Event Staffing specialise in placing people at multiple festivals. Sign up with a few and you'll get a steady stream of opportunities.

### PeakWave
Set your availability for festival season on PeakWave and add festival-related roles to your profile. Employers and event companies looking for seasonal staff will find you. [Create your free profile](/signup) or [browse festival opportunities](/browse).

## What to expect

### Before the festival
You'll usually arrive 1 to 2 days before gates open for setup and briefings. This is when you meet your team, learn the layout, and get your credentials (wristband, staff pass, etc.).

### During the festival
Shifts are typically 8 to 12 hours. You'll work hard during your shifts but have time off to explore the festival, watch acts, and enjoy the atmosphere. Most festivals provide staff camping areas (separate from the public, usually quieter and with better facilities) and at least one hot meal per shift.

### After the festival
Some roles require staying for breakdown: dismantling bars, clearing litter, packing up equipment. This usually adds 1 to 2 days and is paid.

### What to bring
- Wellies (essential, even in summer)
- Tent and sleeping bag
- Warm layers (nights are cold, even in July)
- Waterproof jacket
- Torch/headtorch
- Phone charger (portable battery pack)
- Cash (card machines can be unreliable on site)
- Sun cream and hat (optimistic but necessary)

## Festival work as a stepping stone

Many people start with festival work and move into full-time event industry careers. The skills you build (working under pressure, teamwork, customer service, adaptability) transfer directly to other seasonal roles and beyond.

Festival work also pairs brilliantly with other seasonal work. A winter ski season followed by a summer of festivals is a popular combination on PeakWave.

Ready to get started? [Create your profile on PeakWave](/signup) and set your availability for festival season.`,
  },
  {
    slug: "how-much-money-ski-season",
    title: "How Much Money Do You Need for a Ski Season?",
    excerpt:
      "A realistic budget breakdown for a ski season. What to save before you go, what you'll earn, what you'll spend, and how to avoid running out of cash.",
    category: "tips",
    emoji: "💶",
    readTime: "7 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## The honest answer

The amount you need depends on your role, your resort, and your lifestyle. But as a rough guide: if your job includes accommodation, food, and a lift pass, you can get by with surprisingly little. If it doesn't, you need significantly more.

Here's a realistic breakdown for a typical first season in the French Alps.

## Before you go

### Startup costs

These are one-off expenses before you even arrive:

- **Flights**: £50–£200 (budget airlines to Geneva or Lyon)
- **Travel insurance**: £80–£150 for a full season policy with winter sports cover
- **Ski gear**: £0 if renting in resort (€150–€300 for the season), or £200–£500 if buying your own second-hand
- **Clothing**: £150–£400 depending on what you already own. See our <a href="/guides/what-to-wear-skiing" style="color:#1C8A99">layering guide</a>
- **Food Hygiene certificate**: £15–£25 (online, do it before you go)
- **Visa costs**: Varies. French visa is around €99 for the application, plus your employer handles the work permit
- **Buffer savings**: At least £500–£800 in the bank as a safety net

**Total startup: roughly £800–£1,800** depending on your situation. The biggest variable is gear. If you already own a ski jacket and trousers, that drops significantly.

### The buffer is non-negotiable

Your first pay cheque might not arrive until mid-January if you start in December. You'll need money for the first few weeks: transport from the airport, a drink at the local bar, any kit you forgot, and small expenses that add up. Running out of money in week two is stressful and avoidable.

## What you'll earn

### Typical monthly pay (French Alps)

- **Chalet host**: €1,200–€1,600/month
- **Chalet chef**: €1,800–€2,500/month
- **Bar staff**: €1,000–€1,400/month
- **Hotel reception/housekeeping**: €1,200–€1,500/month
- **Ski instructor**: Variable. BASI Level 2 instructors typically earn €1,500–€2,500/month during peak weeks
- **Driver/transfer**: €1,200–€1,600/month

Most chalet roles include accommodation, food (at least some meals), and a lift pass. This is enormous. If you add up the value of a room, daily meals, and a season pass, it's easily €1,500–€2,000/month in benefits on top of your salary.

### After deductions

French tax and social security will take roughly 20–25% of your gross pay. So a €1,400/month chalet host might take home around €1,050–€1,100. This varies, so ask your employer about net pay.

## What you'll spend

### With accommodation and food included

If your job covers the big three (room, food, lift pass), your monthly spending is essentially discretionary:

- **Drinks and nightlife**: €200–€400 (the biggest variable by far)
- **Eating out**: €50–€100 (staff meals cover most days, but you'll want a pizza night occasionally)
- **Phone**: €10–€20 (a French SIM or roaming add-on)
- **Transport around resort**: €0–€50 (most resorts are walkable)
- **Miscellaneous**: €50–€100 (laundry, toiletries, gear maintenance)

**Monthly total: roughly €300–€700.** The difference between the low and high end is almost entirely nightlife.

### Without accommodation included

If your role doesn't include accommodation (common for bar staff, some hotel roles), add:

- **Shared room in staff housing**: €300–€600/month
- **Food**: €200–€300/month if you're cooking at home

This changes the maths completely. Without housing, you need either higher pay or lower spending to make it work.

## The bottom line

### With accommodation included

You could arrive with £800–£1,000 in savings, earn €1,200–€1,600/month, and finish the season having broken even or saved a small amount. Many people finish a season with €1,000–€3,000 saved, depending on how often they hit the bars.

### Without accommodation

You'll need more savings upfront (£1,500–£2,000 minimum) and careful budgeting throughout. It's absolutely doable, but requires more discipline.

### The biggest financial risk

It's not the cost of living. It's the social spending. A night out in a ski resort can easily cost €50–€80. Do that three times a week and you're spending €600–€1,000/month just on drinks. Most people who struggle financially on a season don't have an income problem. They have a nightlife problem.

Set a weekly budget for going out and stick to it. Pre-drinks at home, staff nights with discounts, and the occasional quiet evening in will make a huge difference.

## Is it worth it?

Financially, a ski season isn't going to make you rich. But with accommodation and food included, almost everything you earn is yours. You'll likely spend less in a ski resort than you would living in a UK city, while having one of the best experiences of your life.

The real cost of a season isn't money. It's the career gap (which most employers understand and many respect) and the risk that you'll enjoy it so much you go back for another one.

<a href="/signup?intent=work&amp;next=%2Fjobs&amp;source=blog" style="color:#1C8A99">Create your PeakWave profile</a> and start planning your season budget.`,
  },
  {
    slug: "ski-season-cv-tips",
    title: "How to Write a Ski Season CV That Gets You Hired",
    excerpt:
      "Your season CV isn't your corporate CV. One page, transferable skills, and a cover note that shows you're a real person. Here's exactly how to write one.",
    category: "tips",
    emoji: "📝",
    readTime: "6 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## Forget your corporate CV

A season CV is not the same document you'd send to a graduate scheme. Seasonal employers don't care about your degree classification, your predicted A-level grades, or the fact that you "worked well within a team environment" at a management consultancy.

They care about three things: Can you do the job? Will you stick it out? Are you someone they'd want to live and work with for four months?

Your CV needs to answer those questions in one page.

## The format

### Keep it to one page

This isn't optional. Seasonal employers are reviewing dozens of applications. They'll spend 30 seconds on yours. One page, clean layout, no filler.

### Lead with availability

Put this right at the top, under your name:

**"Available for chalet host roles, French Alps, December 2026 – April 2027."**

This immediately tells the employer you're a match for their timeline. It's the single most important piece of information on your CV.

### Structure

1. **Contact details**: Name, email, phone, nationality, age (standard in the season world)
2. **Availability statement**: Role, location preference, dates
3. **Key skills & qualifications**: Food Hygiene, driving licence, first aid, languages, STCW (for yachting)
4. **Work experience**: Most recent first. Focus on transferable skills
5. **About me**: 2–3 sentences. Why you want a season, what you bring

## Making no experience sound like experience

### Reframe everything

You've probably done more relevant work than you think. The trick is framing it in terms that matter to seasonal employers:

- **Retail**: Customer service under pressure, cash handling, working to targets
- **Pub/bar work**: Hospitality experience, working evenings and weekends, dealing with difficult customers
- **Babysitting/nannying**: Childcare experience (directly relevant for chalet nanny roles)
- **Cooking for friends/flatmates**: Catering for groups, menu planning, working to dietary requirements
- **University society events**: Event management, teamwork, organisation
- **Volunteering**: Reliability, initiative, working without supervision

### Be specific

Don't say "I have excellent communication skills." Say "I served 50+ customers per shift at a busy pub in central London." Specifics are convincing. Vague claims are invisible.

## The cover note

### This is where you win the job

For first-timers especially, the cover note matters more than the CV. It's your chance to sound like a real person, not a template.

Write 3–4 paragraphs:

1. **Who you are and what you're looking for.** "I'm a 22-year-old from Bristol looking for my first chalet host role this winter in Méribel or Val d'Isère."
2. **Why you want to do a season.** Be honest. The mountains, the lifestyle, the challenge. But show you understand it's hard work, not a holiday.
3. **What you bring.** Transferable skills, relevant qualifications, your attitude. "I've worked in customer-facing roles for three years and I'm used to long hours on my feet."
4. **Commitment.** "I can commit to the full season from early December to late April."

### What NOT to write

- "I just want to ski for a season." (Everyone does. That's not a selling point.)
- "I'm a hard worker and a team player." (Show it, don't say it.)
- Generic templates where you've only changed the company name. Employers spot these instantly.

## Common mistakes

- **CV is too long**: More than one page means you haven't edited ruthlessly enough
- **No availability dates**: The employer doesn't know if you're a match
- **Photo in formal business attire**: A friendly, natural photo is fine. A suit-and-tie headshot looks out of place
- **Listing every GCSE**: Nobody cares. Include your highest qualification if it's relevant, skip the rest
- **Typos and formatting errors**: Print it out and read it. Then get someone else to read it. A sloppy CV suggests a sloppy worker

## One more thing

Make sure you're discoverable. Having a great CV is important, but many seasonal employers find candidates rather than the other way around. <a href="/signup?intent=work&amp;next=%2Fjobs&amp;source=blog" style="color:#1C8A99">Create your PeakWave profile</a> with your availability and skills, and let employers come to you.`,
  },
  {
    slug: "day-in-the-life-ski-season",
    title: "A Day in the Life of a Ski Season Worker",
    excerpt:
      "What does a typical day actually look like on a ski season? The early starts, the ski time, the work, the nights out. An honest hour-by-hour breakdown.",
    category: "winter",
    emoji: "⛷️",
    readTime: "6 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## There's no such thing as a typical day

Every role, every resort, and every company is different. But there are patterns. Here's what a day actually looks like for the most common ski season roles, from the alarm going off to closing time.

## Chalet host

The most common first-season role, and the one with the most distinctive rhythm.

### The schedule

- **6:30am**: Alarm. Nobody bounces out of bed at this point in the season
- **7:00am**: Start setting up breakfast. Table laid, coffee on, bread in the oven, eggs ready
- **7:30–9:00am**: Breakfast service. Guests drift down at different times. You're pouring coffee, answering questions about the ski area, and keeping everything topped up
- **9:00–10:00am**: Clear breakfast, wash up, start cleaning. This is the hard bit. Bathrooms, bedrooms, hoovering, dusting, laundry. The chalet needs to look immaculate every single day
- **10:00am–3:30pm**: FREE TIME. This is why you're here. Get changed, grab your gear, and get on the mountain. Five hours of skiing or snowboarding while guests are out doing the same thing. This is the bit nobody tells you about in the job description
- **3:30–4:30pm**: Back for afternoon tea service. Cake (that you baked yesterday), tea, coffee. Guests return from the slopes tired and hungry
- **5:00–7:30pm**: Prep time. Help the chef with dinner prep if needed, set the dining table, get everything ready for evening service
- **7:30–9:30pm**: Dinner service. You're serving 3–4 courses that the chef has prepared. Wine pouring, clearing plates, chatting with guests
- **9:30–10:30pm**: Clear down, wash up, prep for tomorrow morning
- **10:30pm onwards**: Your evening. The bar, a quiet night in, or sleep if you're smart

### The reality

It's a split shift with a massive ski break in the middle. The work itself is cleaning, serving, and smiling. It's physically demanding. But the payoff is skiing every day in some of the best terrain in the world, with accommodation, food, and often a lift pass included.

## Bar staff

A completely different rhythm.

### The schedule

- **9:00am–12:00pm**: Wake up (your own schedule). Ski. This is your golden time, and it's the main perk of bar work. First lifts with empty pistes
- **12:00–2:00pm**: Lunch, relax, maybe more skiing if you're keen
- **3:00–4:00pm**: Arrive for your shift. Après-ski is kicking off. The bar is filling up with people fresh off the mountain
- **4:00–7:00pm**: Après rush. This is the busiest, most energetic part of the day. Music, shots, dancing on tables (in some bars). You're pouring drinks as fast as you can
- **7:00–8:00pm**: Quieter period. Some people eat, the après crowd thins out
- **8:00pm–1:00am**: Evening service. The pace depends on the bar. Some are pumping until 2am, others wind down by midnight
- **1:00–2:00am**: Close up. Clean, cash up, go home

### The reality

Morning ski time is the trade-off for late nights. You'll ski more than chalet workers because your mornings are completely free. But the late finishes combined with early skiing catches up with everyone eventually. Pace yourself or you'll burn out by February.

## Ski instructor

The role everyone envies (until they hear about the early starts).

### The schedule

- **7:30am**: Wake up. Check conditions, plan the day's lessons
- **8:30am**: Arrive at the ski school meeting point. Get assigned your group
- **9:00am–12:00pm**: Morning lessons. Teaching beginners on the nursery slopes or guiding intermediates around the mountain
- **12:00–1:30pm**: Lunch break. Ski with other instructors or grab food
- **1:30–4:00pm**: Afternoon lessons. Often private clients or advanced groups
- **4:00–5:00pm**: Wind down. Debrief, plan tomorrow, any admin
- **5:00pm onwards**: Free. Après, gym, rest, or evening ski if the resort has night skiing

### The reality

Instructors ski all day, but it's teaching, not freeriding. You'll spend a lot of time on green and blue runs with beginners, which is rewarding but not the powder days you see on Instagram. The best skiing happens on days off, when you can actually choose where to go.

## What everyone has in common

Regardless of role, there are some universal truths about ski season days:

- **The altitude gets you.** Everything is harder at 1,500m+. You'll be more tired, more thirsty, and hungrier than at sea level
- **The routine becomes comforting.** After the first few weeks, the rhythm of work-ski-work-sleep becomes your normal. It's strangely satisfying
- **Days off are sacred.** Most roles give you 1–2 days off per week. These are for skiing the big stuff, exploring neighbouring resorts, or (honestly) sleeping in
- **The social life is intense.** You're living and working with the same people. Friendships form fast and go deep. The après scene is real

The day-to-day of a ski season isn't glamorous. It's hard work punctuated by incredible skiing. But ask anyone who's done one, and they'll tell you it's some of the best months of their life.

Thinking about it? <a href="/signup?intent=work&amp;next=%2Fjobs&amp;source=blog" style="color:#1C8A99">Create your profile on PeakWave</a> and start getting discovered by employers.`,
  },
  {
    slug: "best-summer-season-jobs-abroad",
    title: "Best Summer Season Jobs Abroad in 2026",
    excerpt:
      "The complete guide to summer season work. Yachts, beach clubs, watersports, festivals, and Mediterranean hospitality. Where to go, what to do, and what it pays.",
    category: "summer",
    emoji: "☀️",
    readTime: "8 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## Summer seasons are different

Winter gets all the attention in the seasonal world. Everyone knows about ski seasons. But summer seasons are equally incredible, often better paid, and in many ways more accessible. You don't need to know how to ski. You don't need cold-weather gear. And the destinations are spectacular.

Here's the complete guide to working a summer season abroad.

## Yacht crew

### The role

Working on superyachts is the highest-paying seasonal work available. As a steward or stewardess (the entry-level role), you handle housekeeping, laundry, table service, and looking after guests on board. It's demanding, but the lifestyle and money are hard to beat.

### Where

The Mediterranean (Antibes, Palma, and the Balearics from May to September), the Caribbean (November to May), and Fort Lauderdale (year-round). See our location guides for <a href="/guides/antibes" style="color:#1C8A99">Antibes</a>, <a href="/guides/palma" style="color:#1C8A99">Palma</a>, and <a href="/guides/fort-lauderdale" style="color:#1C8A99">Fort Lauderdale</a>.

### What it pays

Junior stew: €2,000–€3,000/month plus tips. Senior roles and specialist positions pay significantly more. Accommodation and food are included (you live on the boat). Tips during charter season can add €500–€2,000+ per trip.

### Getting started

You'll need an STCW certificate (basic sea safety, ~£800–£1,200, one week). After that, it's about getting your face known in the right marinas. Our <a href="/guides/yacht-crew-jobs-no-experience" style="color:#1C8A99">guide to getting into yachting</a> covers the full process.

## Beach clubs and coastal hospitality

### The role

The Mediterranean equivalent of chalet work. You'll work in bars, restaurants, beach clubs, and hotels across the Greek islands, Spanish coast, Croatian coast, and beyond. Roles include waiting staff, bar staff, reception, and kitchen work.

### Where

- **Greece** (Mykonos, Santorini, Crete, Rhodes): Massive seasonal hiring from April. <a href="/guides/mykonos" style="color:#1C8A99">Read our Mykonos guide</a>
- **Spain** (Ibiza, Barcelona, Mallorca): Party islands and coastal cities. <a href="/guides/ibiza" style="color:#1C8A99">Read our Ibiza guide</a>
- **Croatia** (Split, Hvar, Dubrovnik): Growing fast, great quality of life. <a href="/guides/croatia" style="color:#1C8A99">Read our Croatia guide</a>
- **Portugal** (Algarve, Lisbon): Surf culture meets hospitality

### What it pays

€1,000–€1,800/month. Accommodation sometimes included, sometimes not (check before accepting). Tips can be significant in busy tourist areas. The cost of living is generally lower than ski resorts.

### Getting started

Previous hospitality experience helps but isn't essential for entry-level roles. Many employers hire in person if you turn up early in the season. Having your profile on <a href="/signup?intent=work&amp;next=%2Fjobs&amp;source=blog" style="color:#1C8A99">PeakWave</a> means employers can find you before you even arrive.

## Watersports instruction

### The role

If you have (or can get) watersports qualifications, this is one of the most enjoyable summer jobs in existence. Roles include surf instructor, sailing instructor, kayak guide, dive master, and kitesurf instructor.

### Where

Anywhere with water and tourists. Popular spots include:

- **Portugal and Morocco**: Surfing
- **Greece and Croatia**: Sailing, kayaking, SUP
- **Red Sea (Egypt)**: Diving
- **Thailand and Indonesia**: Diving, surfing
- **UK coastline**: Surf schools in Cornwall, Devon, Wales

### What it pays

€1,200–€2,500/month depending on qualification level and location. Instructors with multiple qualifications (e.g. surf + SUP + kayak) are the most employable.

### Getting started

You'll need qualifications. An ISA surf instructor course takes about two weeks and costs £500–£800. RYA sailing qualifications vary by level. PADI dive instructor is a bigger investment (several months and £2,000+) but opens doors worldwide.

## Festival work

### The role

UK and European festivals hire thousands of temporary staff each summer. Roles range from bar staff and stewards to production crew and artist liaison.

### Where

UK festivals are the biggest market: Glastonbury, Reading & Leeds, Bestival, Boomtown, Latitude, and hundreds of smaller events. European festivals include Primavera Sound (Barcelona), Sonar, and Tomorrowland.

### What it pays

£10–£15/hour plus a free festival ticket for some roles. Some festival companies offer season-long contracts covering multiple events, which gives you consistent work from May to September.

### Getting started

No experience needed for most roles. An SIA licence is required for security/stewarding. Read our <a href="/festivals/festival-jobs-uk" style="color:#1C8A99">full guide to festival jobs</a>.

## Activity and adventure companies

### The role

Summer activity centres and adventure companies hire instructors, group leaders, and support staff for everything from mountain biking to rock climbing, canoeing, and multi-activity holidays for families and school groups.

### Where

The French Alps (summer hiking and biking), UK lakes and national parks, Spain, and Portugal.

### What it pays

€1,000–€1,800/month. Accommodation often included.

### Getting started

Outdoor qualifications help (Mountain Leader, SPA climbing, BCU paddlesport). But many companies also hire general assistants and group leaders without specific qualifications.

## How to choose

Ask yourself:

- **Do you want to be on the water?** Yachting or watersports
- **Do you want nightlife and hospitality?** Beach clubs and Med resort work
- **Do you want festivals and events?** Festival crew and event companies
- **Do you want outdoor adventure?** Activity centres and adventure companies

Or do all of them. Many seasonaires chain a winter ski season with a summer of festivals, yacht work, or island hopping. That's the beauty of seasonal work: it's flexible.

<a href="/signup?intent=work&amp;next=%2Fjobs&amp;source=blog" style="color:#1C8A99">Create your PeakWave profile</a> and set availability for summer. Employers are already looking.`,
  },
  {
    slug: "back-to-back-seasons",
    title: "Back-to-Back Seasons: The Endless Summer/Winter Lifestyle",
    excerpt:
      "How people chain ski seasons with summers abroad, year after year. The logistics, the finances, the reality, and why some people never stop.",
    category: "tips",
    emoji: "🔄",
    readTime: "7 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## The concept

A back-to-back season means finishing one season and rolling straight into another. Ski season in the Alps from December to April, then straight to a yacht in the Med, a beach bar in Greece, or a festival circuit from May to September. Then back to the mountains. Repeat.

Some people do this for a year. Some do it for a decade. A surprising number of them never really stop.

## How it works

### The classic combo: winter Alps, summer Med

This is the most common back-to-back pattern:

- **December–April**: Ski season in France, Austria, or Switzerland. Chalet host, chef, bar staff, instructor
- **May**: Travel buffer. Some people go home briefly, others head straight south
- **June–September**: Yacht crew in Antibes or Palma, beach bar in Ibiza or Mykonos, watersports in Croatia or Portugal
- **October–November**: Travel, rest, or pick up autumn work (NZ ski season runs June–October for a true endless winter)

### Other patterns

- **Alps winter + UK festival summer**: December–April skiing, May–September festival circuit
- **Northern hemisphere winter + southern hemisphere winter**: Alps December–April, then New Zealand or Australia June–October for a double winter
- **Caribbean yacht winter + Med yacht summer**: Follow the superyacht fleet between hemispheres
- **Alps winter + activity centre summer**: Mountains year-round, just different activities

## The logistics

### Visas and work permits

This is the biggest practical consideration. If you're a UK citizen, you'll need work permits for each EU country you work in. Some people hold permits for multiple countries simultaneously. If you have an EU, Irish, or dual passport, this is much simpler.

For non-EU seasons (NZ, Canada, Caribbean), working holiday visas are often available for under-30s or under-35s.

### Where your stuff lives

Experienced back-to-back seasonaires travel light. Everything fits in one rucksack (see our <a href="/guides/packing-for-a-season" style="color:#1C8A99">packing guide</a>). Some leave a box of winter gear at a friend's place or in storage between seasons. Others sell their ski gear at the end of winter and buy fresh for the next year.

The key mindset shift: you don't have a home base. Or rather, your home base moves with you.

### Money

Back-to-back seasons are financially sustainable if your roles include accommodation and food. You're earning consistently with minimal living costs. The gaps between seasons (typically 2–6 weeks) are where you spend savings, so keeping a buffer is important.

Many back-to-back seasonaires actually save more than they would in a city, because their major expenses are covered for 10+ months of the year.

### Relationships and friendships

The seasonal world is remarkably close-knit. You'll meet the same people across different resorts and seasons. The friend you made in Méribel turns up in Antibes. Your old chalet manager is now crewing a yacht in the Caribbean. The seasonal community is smaller than you'd think, and bonds form fast.

Romantic relationships are common but complicated. Long-distance between seasons, or both partners chasing the same circuit. Some couples make it work beautifully. Others find the constant movement too much. It's worth being honest with yourself about what you want.

## The reality check

### The good

- You live in incredible places year-round
- Minimal living expenses (accommodation and food usually included)
- Deep, intense friendships with people from all over the world
- Extraordinary experiences: skiing powder in the morning, swimming in the Med by summer
- Career skills in hospitality, languages, and people management that translate anywhere
- Freedom. No commute, no mortgage, no routine (or rather, a constantly changing routine)

### The honest challenges

- **Career gap perception**: Some industries view extended seasonal work differently. Others (hospitality, tourism, events, outdoor education) see it as directly relevant experience
- **Tax and admin**: Working in multiple countries means dealing with multiple tax systems. Keep records, file returns, and consider speaking to a tax advisor if it gets complex
- **Fatigue**: Seasons are intense. Without breaks, burnout is real. The people who sustain back-to-back seasons long-term are the ones who build in rest periods
- **Ageing out**: The seasonal workforce skews young (18–30), though there are experienced seasonaires of all ages. As you get older, you might find yourself wanting stability. That's completely normal
- **The "what am I doing with my life" moment**: Almost everyone has it at some point, usually around season three or four. It passes. Or it doesn't, and you pivot. Both are fine

## When to stop (or not)

There's no right time. Some people do one back-to-back year and return to a career. Others build their entire working life around seasons. Many end up in permanent roles within the seasonal industry: hotel management, yacht captaincy, tour operator leadership, or starting their own businesses.

The seasonal world rewards experience. A chalet host who's done five winters is running the show. A yacht stew who's done three Med seasons is moving into chief stew roles on bigger boats. There's genuine career progression for those who want it.

The trick is making it a choice, not a default. Do another season because you want to, not because you can't think of anything else.

## Getting started

If you're considering your first back-to-back, start simple: one winter season followed by one summer. See how the transition feels. See if the lifestyle suits you.

<a href="/signup?intent=work&amp;next=%2Fjobs&amp;source=blog" style="color:#1C8A99">Create your PeakWave profile</a> with multiple availability windows for different seasons. Employers in both winter and summer industries are looking.`,
  },
  {
    slug: "preparing-for-ski-season-fitness",
    title: "How to Prepare for a Ski Season: Fitness, Admin, and Mindset",
    excerpt:
      "The practical stuff nobody tells you before your first season. How to get fit, sort the admin, and mentally prepare for months in the mountains.",
    category: "tips",
    emoji: "💪",
    readTime: "7 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## Why preparation matters

The people who have the best seasons aren't the fittest or the most experienced. They're the ones who prepared properly. Not overcommitted, not obsessive, just genuinely ready for what's coming.

A ski season is physically demanding, administratively fiddly, and mentally intense. Here's how to prepare for all three.

## Physical preparation

### You don't need to be an athlete

But you do need basic fitness. A ski season is physically hard in ways you might not expect:

- **Cleaning at altitude**: Hoovering, making beds, and carrying laundry at 1,500m+ is genuinely exhausting for the first few weeks
- **Skiing every day**: Even if you're a good skier, your legs aren't used to skiing for 4–5 hours a day, multiple days in a row
- **Long shifts on your feet**: Bar staff, hotel reception, and kitchen work mean 8–12 hours standing
- **Cold**: Your body burns more calories in the cold. Everything is harder when it's freezing

### What to focus on (6–8 weeks before)

You don't need a gym membership. You need:

- **Legs**: Squats, lunges, wall sits. Skiing is a leg sport. Strong quads and glutes make everything from carving to getting up from a fall easier
- **Core**: Planks, dead bugs, mountain climbers. Core stability prevents injuries and makes you a better skier
- **Cardio**: Running, cycling, swimming. Whatever you enjoy. You don't need to be marathon-fit, but being able to walk uphill without getting breathless makes a difference at altitude
- **Flexibility**: Basic stretching or yoga. Tight hamstrings and hip flexors are a recipe for knee injuries on the slopes

### A simple pre-season routine

Three times a week, for 6–8 weeks:

- 3x15 squats
- 3x12 lunges (each leg)
- 3x30 second wall sits
- 3x30 second planks (front and side)
- 20 minutes of cardio (running, cycling, whatever)
- 10 minutes stretching

That's it. Nothing fancy. The goal is to arrive in resort without your legs giving out in the first week.

### Altitude matters

If you've never spent time above 1,000m, the altitude will hit you. You'll feel more tired, more breathless, and more dehydrated than you expect. It takes about a week for your body to adjust. Drink more water than you think you need, especially in the first few days. Go easy on the alcohol for the first week (easier said than done when everyone's celebrating the start of the season).

## Administrative preparation

### The boring stuff that matters

Get all of this sorted at least a month before you leave. Chasing documents from abroad is stressful and sometimes impossible.

- **Passport**: Check it's valid for at least 6 months beyond your planned return. If it expires during the season, renew it now
- **Visa/work permit**: If you need one, start the process as early as possible. See our <a href="/guides/seasonal-work-visa-guide" style="color:#1C8A99">seasonal work visa guide</a>
- **Insurance**: Get a season-long policy that covers winter sports, including off-piste if you plan to do any. Standard travel insurance usually excludes winter sports. Check the small print
- **EHIC/GHIC card**: Apply for one if you don't have it. It covers emergency treatment in EU countries but doesn't replace travel insurance
- **Bank account**: Set up a multi-currency account (Monzo, Revolut, Wise) if you don't already have one. You'll save hundreds on exchange rates over a season
- **Qualifications**: Get your Food Hygiene certificate done (online, £15–£25, a few hours). If you need a DBS check, apply early as they can take weeks
- **Driving licence**: If you have one, bring it. If you don't and it's practical to get one before your season, consider it. A licence opens up driver roles and makes you more flexible
- **Phone**: Check your mobile plan for EU roaming. Some UK plans include it, others charge. A local SIM is often cheaper for a full season

## Mental preparation

### What to expect

A ski season is brilliant, but it's also intense. You're leaving home, living with strangers, working a physically demanding job, and adjusting to a new country and altitude. All at once.

### The first two weeks are the hardest

Almost everyone finds the first fortnight tough. You're tired from travel, altitude-sick, learning a new job, sleeping in a shared room, and missing home. This is completely normal. Push through it. By week three, it starts to feel like home.

### Homesickness is real

Even if you've travelled before, a season is different because it's long. You're not on holiday. You're living somewhere. Homesickness hits most people at some point, usually around week 2–3 and again in February when the novelty has worn off and the end of the season feels far away.

What helps: FaceTime with home regularly but not obsessively. Throw yourself into the social life. Remember that almost everyone around you is in the same boat.

### The social intensity

Living and working with the same small group of people for four months is intense. You'll make incredible friendships, but you'll also have days where your housemates drive you mad. This is normal. Everyone's in close quarters, working hard, and tired.

Be the person who does the washing up. Be the person who suggests a ski day when everyone's feeling flat. Small acts of kindness go a long way in a small community.

### Say yes

The people who have the best seasons are the ones who say yes to things. The after-work ski with someone you've just met. The trip to the next valley on your day off. The fondue night that sounded boring but turned out to be brilliant.

## A pre-season checklist

Use this as a starting point:

- [ ] Passport valid for full season + 6 months
- [ ] Visa/work permit applied for (if needed)
- [ ] Season travel insurance purchased
- [ ] EHIC/GHIC card applied for
- [ ] Food Hygiene certificate completed
- [ ] Multi-currency bank account set up
- [ ] DBS check applied for (if needed)
- [ ] Phone plan checked for EU roaming
- [ ] Basic fitness routine started (6+ weeks out)
- [ ] Gear sorted (see our <a href="/guides/what-to-wear-skiing" style="color:#1C8A99">layering guide</a> and <a href="/guides/packing-for-a-season" style="color:#1C8A99">packing guide</a>)
- [ ] Documents photographed and stored digitally
- [ ] <a href="/signup?intent=work&amp;next=%2Fjobs&amp;source=blog" style="color:#1C8A99">PeakWave profile created</a> with availability

You're ready. See you in the mountains.`,
  },
  {
    slug: "what-is-working-a-festival-really-like",
    title: "What Is Working a Festival Really Like?",
    excerpt:
      "The mud, the magic, the 14-hour shifts. An honest look at what it's actually like to work behind the scenes at UK festivals.",
    category: "summer",
    emoji: "🎪",
    readTime: "7 min read",
    author: "Josh, Founder",
    date: "Mar 2026",
    content: `## It's not what you think

When people imagine working a festival, they picture watching bands from backstage with a free beer. The reality is different. Festival work is physically exhausting, unpredictable, often muddy, and occasionally chaotic. It's also some of the most fun you'll ever have at work.

Here's what it's actually like.

## The build-up

### Arriving before anyone else

Most festival workers arrive days before the gates open. The site is empty, just fields and infrastructure taking shape. You'll watch a bare patch of countryside transform into a small city. It's genuinely impressive.

Build days involve setting up whatever your role requires: assembling bar structures, testing equipment, marking out areas, stocking supplies, briefing teams. It's long hours and physical work, but the atmosphere is buzzing because everyone knows what's coming.

### The calm before the storm

There's a strange, beautiful moment the day before gates open. The site is complete but empty. You walk through the finished festival knowing that in 24 hours it'll be filled with tens of thousands of people. It never gets old.

## During the festival

### The hours

Festival shifts are long. 10–14 hours is standard during the event itself. If you're on bar staff, you might work from noon until 2am. If you're stewarding, you could be on an early morning gate shift followed by a late arena shift. Production crew often work from dawn to well after midnight.

You'll get breaks, and you'll get some time off to explore the festival. But make no mistake: this is hard work, not a free ticket with occasional duties.

### Bar staff

The most common festival role. You'll be pouring pints and mixing drinks in a temporary bar, often in a tent or outdoor structure. The pace ranges from steady to absolutely frantic.

A typical day:

- **10:00am**: Arrive for your shift. Stock the bars, check the fridges, prep the cocktail station
- **12:00pm**: Bars open. The first customers are gentle. Day drinkers, people having lunch
- **3:00–6:00pm**: Getting busy. Headliners are playing, the crowd is building
- **7:00–11:00pm**: Full intensity. Three-deep at the bar, shouting over the music, pouring as fast as you can
- **11:00pm–1:00am**: Winding down (or not, depending on the arena). Last orders, clean-up, cash out
- **1:00–2:00am**: Close. Clean the bars, stock for tomorrow, collapse

### Stewarding

You're managing crowds, checking wristbands, giving directions, and making sure people are safe. It's less physically intense than bar work but mentally demanding. You need to stay alert for long periods, deal with drunk or distressed people, and make quick decisions.

The best bit about stewarding: you're usually positioned where you can see and hear the performances. The worst bit: you can't properly enjoy them because you're working.

### Production and backstage

If you're on production crew (stage management, lighting, sound, rigging), the hours are the longest but the experience is the most unique. You'll see how festivals actually work from the inside. You'll meet artists and their teams. And you'll develop technical skills that translate directly into full-time events careers.

## The conditions

### Weather

This is the UK. Assume rain. Even at summer festivals, you can get days of solid rain that turn the site into a mud bath. You'll work through it. Wellies aren't a fashion choice; they're survival equipment.

But when the sun comes out at a festival, there's nothing like it. A sunny Saturday afternoon with your favourite band playing while you serve drinks to happy people is close to perfect.

### Camping

You'll camp on-site, usually in a crew camping area. This means a tent, a sleeping bag, and whatever comfort items you brought. Some festivals have nicer crew areas (showers, charging points, a quiet zone). Others are basic.

Sleep is limited. The music, the atmosphere, and the energy of the site make it hard to sleep before midnight even when you're exhausted. And if you're on an early shift, your alarm is going off at 7am regardless.

### Food

Most festivals feed their crew, ranging from decent to actually good. Some have dedicated crew catering areas with hot meals. Others give you vouchers for the food stalls. Either way, you won't go hungry, but you might get tired of the same options by day three.

## The people

### Your crew

You'll work alongside a mix of festival veterans and first-timers. The veterans know every shortcut and every trick. The first-timers bring enthusiasm and fresh energy. Within 24 hours, you'll feel like you've known these people for years. There's something about shared hard work and shared euphoria that bonds people fast.

### The crowd

Festival-goers are, on the whole, wonderful. They're happy, they're on holiday, they're grateful when you pour their drinks. You'll have funny conversations, get unsolicited life advice from strangers, and witness some genuinely bizarre behaviour (in a good way, mostly).

You'll also deal with people who are too drunk, too high, or too sunburnt. This is where the work gets real. Being patient and kind when someone is struggling takes energy, but it's part of the job.

## After the festival

### The comedown

When the music stops and the crowds leave, the site feels surreal. Empty stages, abandoned tents, trampled fields. The crew stays for the breakdown, which can take days. It's quieter, more reflective, and physically heavy work (taking apart everything you built).

The post-festival feeling is hard to describe. A mix of exhaustion, satisfaction, and genuine sadness that it's over. Most people sleep for about 14 hours straight when they finally get home.

### Is it worth it?

Yes. Festival work is a unique experience that combines physical challenge, creative energy, and social intensity in a way that no other job does. It's not for everyone, and it's not a relaxing summer job. But if you thrive on energy, unpredictability, and being part of something bigger, there's nothing quite like it.

### Combining with other seasonal work

For roles, pay, hiring timelines and who to apply to, read our main <a href="/festivals/festival-jobs-uk" style="color:#1C8A99">UK festival jobs guide</a>.

Festival work pairs naturally with other seasonal roles. A winter ski season followed by a summer of festivals is one of the most popular combinations. Set your availability on <a href="/signup?intent=work&amp;next=%2Fjobs&amp;source=blog" style="color:#1C8A99">PeakWave</a> for both and keep your options open.`,
  },
];

const RETIRED_BLOG_SLUGS = new Set(CONSOLIDATED_BLOG_SLUGS);

export const BLOG_POSTS: BlogPost[] = ALL_BLOG_POSTS.filter(
  (post) => !RETIRED_BLOG_SLUGS.has(post.slug),
);

export function getPostBySlug(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug);
}

export function getPostsByCategory(
  category: BlogCategory | "all"
): BlogPost[] {
  if (category === "all") return BLOG_POSTS;
  return BLOG_POSTS.filter((p) => p.category === category);
}
